import { useState, useEffect, useRef } from 'react';
import WalletCard from './WalletCard';
import AddAccountForm from './AddAccountForm';
import AccountAddModal from './AccountAddModal';
import StatementUpload from './StatementUpload';
import PlaidLink from '../../../components/shared/PlaidLink';
import SyncButton from './SyncButton';
import walletService from '../services/walletService';
import WalletStatsDashboard from './WalletStatsDashboard';
import PartnerInfoCard from '../../../components/shared/PartnerInfoCard';
import SharingManagementModal from '../../../components/shared/SharingManagementModal';
import AccountSharingModal from './AccountSharingModal';
import MobileViewSelector from '../../../components/shared/MobileViewSelector';
import MobileIconSelector from '../../../components/shared/MobileIconSelector';
import { useAuthContext } from '../../../features/auth/context/AuthContext';
import StatementImportModal from './StatementImportModal';
import { 
  PlusIcon, 
  BuildingLibraryIcon, 
  ArrowPathIcon, 
  XMarkIcon, 
  UserIcon, 
  UserGroupIcon, 
  ShareIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationCircleIcon, 
  CreditCardIcon, 
  CalculatorIcon, 
  ChartBarIcon, 
  HomeIcon, 
  BanknotesIcon, 
  InformationCircleIcon, 
  LightBulbIcon,
  CogIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  QuestionMarkCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../../utils/api';
import PlaidConnectionOptions from './PlaidConnectionOptions';
import Portal from '../../../components/shared/Portal';
import budgetService from '../../budgeting/services/budgetService';
import transactionService from '../../transactions/services/transactionService';
import { isLoanComplete } from '../utils/loanCompletionUtils';

/**
 * Wallet Component
 * 
 * Displays user's financial accounts with categorization, filtering, and partner sharing features.
 * Integrates with Plaid for bank account connections and provides manual account management.
 */

// Debug flag - set to true to enable detailed logging
// When enabled, shows verbose console logs for debugging sync, filtering, and partner data
const DEBUG_WALLET = false; // ⚠️ Set to true only for debugging - causes infinite loops

const Wallet = () => {
  const { user, refreshUserData, getPartnerInvitations } = useAuthContext();
  const [accounts, setAccounts] = useState({ personal: [], joint: [], partner: [] });
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [partnerAccounts, setPartnerAccounts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPlaidLink, setShowPlaidLink] = useState(false);
  const [reconnectItemId, setReconnectItemId] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  
  // Store refs for dropdown buttons and containers
  const buttonRefs = useRef({});
  const dropdownRefs = useRef({});
  
  // Calculate dropdown position based on button position
  const calculateDropdownPosition = (institutionName) => {
    const button = buttonRefs.current[institutionName];
    if (!button) return null;
    const rect = button.getBoundingClientRect();
    return {
      top: `${rect.bottom + window.scrollY + 8}px`,
      right: `${window.innerWidth - rect.right}px`,
    };
  };
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown container or button
      let clickedInside = false;
      Object.values(buttonRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInside = true;
        }
      });
      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInside = true;
        }
      });
      if (!clickedInside) {
        setOpenDropdowns({});
        setDropdownPositions({});
      }
    };
    // Use mousedown instead of click to avoid conflicts
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // Type filter: all, checking, savings, etc.
  const [activeViewTab, setActiveViewTab] = useState('my'); // View filter: my, our, partner
  const [hasPartner, setHasPartner] = useState(false);
  const [partnerName, setPartnerName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [showAccountSharingModal, setShowAccountSharingModal] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState('connected'); // connected, pending, disconnected
  const [syncStatus, setSyncStatus] = useState({});
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(true);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  // Map of institutionName -> { logo, primaryColor }
  const [institutionBranding, setInstitutionBranding] = useState({});
  const [initialInstitutionStatus, setInitialInstitutionStatus] = useState({});
  // Side-rail data
  const [budgetStats, setBudgetStats] = useState(null);
  const [expenseStats, setExpenseStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  
  // Statement Import state
  const [showStatementImportModal, setShowStatementImportModal] = useState(false);
  
  // Toggle for including/excluding mortgages from total debt calculation
  const [includeMortgagesInDebt, setIncludeMortgagesInDebt] = useState(() => {
    const saved = localStorage.getItem('includeMortgagesInDebt');
    return saved !== null ? JSON.parse(saved) : false; // Default to false (exclude mortgages)
  });

  // Toggle for including/excluding mortgages from net worth calculation
  const [includeMortgagesInNetWorth, setIncludeMortgagesInNetWorth] = useState(() => {
    const saved = localStorage.getItem('includeMortgagesInNetWorth');
    return saved !== null ? JSON.parse(saved) : true; // Default to true (include mortgages in net worth)
  });

  // Persist mortgage toggle preference
  useEffect(() => {
    localStorage.setItem('includeMortgagesInDebt', JSON.stringify(includeMortgagesInDebt));
  }, [includeMortgagesInDebt]);

  // Persist net worth mortgage toggle preference
  useEffect(() => {
    localStorage.setItem('includeMortgagesInNetWorth', JSON.stringify(includeMortgagesInNetWorth));
  }, [includeMortgagesInNetWorth]);

  useEffect(() => {
    fetchWalletData();
    fetchInvitations();
    // Side-rail data (best-effort)
    (async () => {
      try {
        const b = await budgetService.getBudgetStatistics();
        setBudgetStats(b);
      } catch {}
      try {
        const e = await transactionService.getExpenseStatistics();
        if (e?.data) setExpenseStats(e.data);
      } catch {}
      try {
        const tx = await transactionService.getAllTransactions({ limit: 10, sortBy: 'date', sortDirection: 'desc' });
        setRecentTransactions(tx?.data?.transactions?.slice(0, 5) || []);
      } catch {}
    })();
    
    // Load stored sync status immediately on mount
    const storedSyncStatus = localStorage.getItem('plaidSyncStatus');
    if (storedSyncStatus) {
      try {
        const parsedStatus = JSON.parse(storedSyncStatus);
        
        // Only set sync status if it's not empty and has valid data
        if (parsedStatus && Object.keys(parsedStatus).length > 0) {
          setSyncStatus(parsedStatus);
        } else {
          localStorage.removeItem('plaidSyncStatus');
          localStorage.removeItem('lastPlaidSync');
        }
      } catch (error) {
        console.error('Error parsing stored sync status on mount:', error);
        // Clear corrupted data
        localStorage.removeItem('plaidSyncStatus');
        localStorage.removeItem('lastPlaidSync');
      }
    }
    
    // Close all dropdowns when clicking outside
    const handleClickOutside = (event) => {
      const dropdowns = document.querySelectorAll('[id^="dropdown-"]');
      dropdowns.forEach(dropdown => {
        if (!dropdown.contains(event.target) && !event.target.closest('[title="Institution Settings"]')) {
          dropdown.classList.add('hidden');
        }
      });
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Auto-sync Plaid accounts on page load/refresh (once per day)
  useEffect(() => {
    const autoSyncPlaidAccounts = async () => {
      try {
        // Check if we've already synced today
        const lastSyncTime = localStorage.getItem('lastPlaidSync');
        const now = Date.now();
        const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000); // 24 hours in milliseconds
        
        if (lastSyncTime && parseInt(lastSyncTime) > twentyFourHoursAgo) {
          // Load existing sync status from localStorage since we're not re-syncing
          const storedSyncStatus = localStorage.getItem('plaidSyncStatus');
          if (storedSyncStatus) {
            try {
              const parsedStatus = JSON.parse(storedSyncStatus);
              setSyncStatus(parsedStatus);
            } catch (error) {
              console.error('Error parsing stored sync status:', error);
            }
          }
          
          return;
        }
        
        // Batch auto-sync via server endpoint with extended timeout
        const batch = await api.post('/api/plaid/sync-all', {}, {
          headers: { 'X-Sync-Source': 'auto' },
          timeout: 120000
        });
        const results = batch?.data?.results || [];
        const syncResults = {};
        results.forEach(r => {
          const name = r.institutionName || r.itemId || 'Unknown Institution';
          if (r.success) {
            syncResults[name] = {
              success: true,
              lastSync: new Date().toISOString(),
              syncType: 'auto'
            };
          } else {
            const status = r.status || r.httpStatus;
            const code = r.error_code || r.code;
            const isRateLimited = status === 429 || code === 'RATE_LIMITED';
            syncResults[name] = {
              success: false,
              error: isRateLimited ? 'RATE_LIMITED' : (code === 'ITEM_LOGIN_REQUIRED' ? 'CONNECTION_EXPIRED' : (r.error_type || 'SYNC_ERROR')),
              message: r.error || r.message || 'Failed to sync accounts.',
              lastSync: new Date().toISOString(),
              syncType: 'auto'
            };
            if (code === 'ITEM_LOGIN_REQUIRED') {
              toast.error(`${name} connection expired. Please reconnect your account.`, { duration: 8000, });
            }
          }
        });
        
        // Reconcile with Plaid items: if item status is good, mark connected
        try {
          const itemsRes = await api.get('/api/plaid/items');
          const items = itemsRes?.data || [];
          items.forEach(item => {
            const name = item.institutionName;
            if (!name) return;
            if (item.status === 'good') {
              syncResults[name] = {
                success: true,
                lastSync: new Date().toISOString(),
                syncType: 'auto'
              };
            }
          });
        } catch (_) {}

        // Store sync results for UI display
        setSyncStatus(syncResults);
        
        // Store sync status in localStorage for persistence
        localStorage.setItem('plaidSyncStatus', JSON.stringify(syncResults));
        
        // Update last sync time
        localStorage.setItem('lastPlaidSync', now.toString());
        
        // Refresh the account list after auto-sync
        await fetchWalletData();
      } catch (error) {
        console.error('Error during auto-sync:', error);
        // Don't show error toast for network issues to avoid annoying users
      }
    };

    // Run auto-sync after a short delay to let the component fully mount
    const timer = setTimeout(autoSyncPlaidAccounts, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Initialize transaction details state based on existing shared accounts
  useEffect(() => {
    if (accounts.personal && accounts.personal.length > 0) {
      const sharedAccounts = accounts.personal.filter(account => {
        return account.sharedWithPartner || 
               account.visibilitySettings?.showToPartner || 
               account.metadata?.sharingSettings?.showToPartner;
      });
      
      if (sharedAccounts.length > 0) {
        // Check if any shared account has transaction sharing enabled
        const hasTransactionSharing = sharedAccounts.some(account => {
          return account.visibilitySettings?.showTransactions || 
                 account.metadata?.sharingSettings?.showTransactions;
        });
        
        // console.log('🔍 Initializing transaction details state:', {
        //   sharedAccountsCount: sharedAccounts.length,
        //   hasTransactionSharing,
        //   accounts: sharedAccounts.map(acc => ({
        //     name: acc.name || acc.bankName,
        //     showTransactions: acc.visibilitySettings?.showTransactions || acc.metadata?.sharingSettings?.showTransactions
        //   }))
        // });
        
        setShowTransactionDetails(hasTransactionSharing);
      }
    }
  }, [accounts.personal]);

  // Debug function to show current sync status
  const debugSyncStatus = () => {
    if (!DEBUG_WALLET) return;
    
    const lastSync = localStorage.getItem('lastPlaidSync');
    const storedStatus = localStorage.getItem('plaidSyncStatus');
    
    console.log('🔍 DEBUG SYNC STATUS:');
    console.log('- Last sync timestamp:', lastSync ? new Date(parseInt(lastSync)).toLocaleString() : 'Never');
    console.log('- Stored sync status:', storedStatus || 'None');
    console.log('- Current sync status state:', syncStatus);
    console.log('- Time since last sync:', lastSync ? ((Date.now() - parseInt(lastSync)) / 1000 / 60 / 60).toFixed(2) + ' hours' : 'N/A');
    console.log('- Raw localStorage lastPlaidSync:', localStorage.getItem('lastPlaidSync'));
    console.log('- Raw localStorage plaidSyncStatus:', localStorage.getItem('plaidSyncStatus'));
    
    // Debug account institution names vs sync status keys
    console.log('\n🏦 INSTITUTION NAME MATCHING DEBUG:');
    console.log('- Sync Status Keys:', Object.keys(syncStatus));
    
    const allAccounts = [...(accounts.personal || []), ...(accounts.joint || []), ...(accounts.partner || [])];
    const plaidAccounts = allAccounts.filter(acc => acc.isPlaidAccount);
    
    console.log('- Plaid Account Institution Names:');
    plaidAccounts.forEach((acc, idx) => {
      const institutionName = acc.institutionName || acc.bankName || 'Unknown Bank';
      const hasMatch = !!syncStatus[institutionName];
      console.log(`  ${idx + 1}. "${institutionName}" (Account: ${acc.name || acc.bankName}) - Match: ${hasMatch ? '✅' : '❌'}`);
      
      if (!hasMatch) {
        // Check for potential fuzzy matches
        const syncKeys = Object.keys(syncStatus);
        const potentialMatches = syncKeys.filter(key => {
          const keyLower = key.toLowerCase();
          const nameLower = institutionName.toLowerCase();
          return keyLower.includes(nameLower) || nameLower.includes(keyLower);
        });
        
        if (potentialMatches.length > 0) {
          console.log(`    Potential fuzzy matches: ${potentialMatches.join(', ')}`);
        }
      }
    });
  };

  // Function to clear old sync data
  const clearOldSyncData = () => {
    if (DEBUG_WALLET) console.log('🧹 Clearing old sync data...');
    localStorage.removeItem('lastPlaidSync');
    localStorage.removeItem('plaidSyncStatus');
    setSyncStatus({});
    if (DEBUG_WALLET) console.log('✅ Old sync data cleared - next page load will trigger fresh sync');
  };

  // Add debug logging whenever syncStatus changes
  useEffect(() => {
    if (DEBUG_WALLET) {
      console.log('🔄 Sync status state updated:', syncStatus);
      if (Object.keys(syncStatus).length > 0) {
        console.log('📊 Sync status details:', syncStatus);
      } else {
        console.log('⚠️ Sync status is empty - cards will show Unknown status');
      }
    }
  }, [syncStatus]);

  // Debug effect to run on mount
  useEffect(() => {
    if (DEBUG_WALLET) {
      console.log('🔍 Component mounted, checking initial sync status...');
      debugSyncStatus();
    }
    
    // Add debug functions to window for easy access
    window.debugSyncStatus = debugSyncStatus;
    window.clearOldSyncData = clearOldSyncData;
    if (DEBUG_WALLET) {
      console.log('💡 Debug tips:');
      console.log('  - Run debugSyncStatus() in console to check sync status');
      console.log('  - Run clearOldSyncData() in console to clear old sync data');
    }
  }, []);

  // Fetch partner invitations
  const fetchInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const response = await getPartnerInvitations();
      
      if (response && response.invitations && Array.isArray(response.invitations)) {
        const currentUserId = user?.id;
        const currentUserEmail = user?.email ? user.email.toLowerCase() : null;
        
        const userSentInvitations = response.invitations.filter(inv => {
          const invSenderId = inv.sender?._id || inv.sender;
          const invSenderEmail = inv.sender?.email ? inv.sender.email.toLowerCase() : null;
          
          return (invSenderId && invSenderId === currentUserId) || 
                 (currentUserEmail && invSenderEmail === currentUserEmail);
        });
        
        const userReceivedInvitations = response.invitations.filter(inv => {
          const invRecipientId = inv.recipient?._id || inv.recipient;
          const invRecipientEmail = inv.recipientEmail ? inv.recipientEmail.toLowerCase() : null;
          
          return (invRecipientId && invRecipientId === currentUserId) || 
                 (currentUserEmail && invRecipientEmail === currentUserEmail);
        });
        
        if (DEBUG_WALLET) {
          console.log('Processed invitations:', {
            total: response.invitations.length,
            sent: userSentInvitations.length,
          received: userReceivedInvitations.length,
          sentInvitations: userSentInvitations.map(inv => ({
            id: inv._id,
            status: inv.status,
            recipientEmail: inv.recipientEmail,
            createdAt: inv.createdAt
          })),
          receivedInvitations: userReceivedInvitations.map(inv => ({
            id: inv._id,
            status: inv.status,
            senderEmail: inv.sender?.email,
            createdAt: inv.createdAt
          }))
        });
        }
        
        setSentInvitations(userSentInvitations);
        setReceivedInvitations(userReceivedInvitations);
      }
    } catch (error) {
      console.error('Failed to fetch invitations', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const fetchWalletData = async () => {
    setIsLoading(true);
      setError(null);
      
    try {
      // First try to get regular personal accounts using the original API
      const personalResponse = await walletService.getAccounts();
      
      if (personalResponse.success) {
        // Debug: Log account sharing settings (reduced logging)
        if (DEBUG_WALLET && personalResponse.data?.length > 0) {
          console.log('Account sharing summary:', {
            totalAccounts: personalResponse.data.length,
            sharedAccounts: personalResponse.data.filter(acc => acc.sharedWithPartner).length,
            jointAccounts: personalResponse.data.filter(acc => acc.isJointAccount).length
          });
        }
        
        // Set accounts in the expected format
        // Partner accounts are now included in the response from the wallet API
        const partnerAccountsFromResponse = personalResponse.partnerAccounts || [];
        
        setAccounts({
          personal: personalResponse.data || [],
          joint: [], // Joint accounts will be handled separately if partner features are enabled
          partner: partnerAccountsFromResponse
        });
        
        // Check if user has a partner to determine if we need partner data
        const userHasPartner = !!user?.partner || !!user?.createdPartner || partnerAccountsFromResponse.length > 0;
        setHasPartner(userHasPartner);
        
        // Set partner name for display
        if (user?.partner && typeof user.partner === 'object') {
          setPartnerName(user.partner.firstName || null);
        } else if (user?.createdPartner && typeof user.createdPartner === 'object') {
          setPartnerName(user.createdPartner.firstName || null);
        } else {
          setPartnerName(null);
        }
        
        // In parallel, fetch Plaid accounts to build institution branding map
        try {
          // Helper to normalize institution names for fuzzy matching
          const clean = (str = '') => (str || '')
            .toLowerCase()
            .replace(/\./g, '')
            .replace(/\b(national|association|assn|bank|banking|usa|us|u\s*s|na|n\s*a)\b/g, '')
            .replace(/[^a-z0-9]+/g, '')
            .trim();

          // Build branding map from Plaid accounts
          const plaidAccountsRes = await api.get('/api/plaid/accounts');
          const brandingMap = {};
          (plaidAccountsRes.data || []).forEach(acc => {
            const rawName = acc?.institutionBranding?.name || acc?.itemId?.institutionName || acc?.institutionName || acc?.bankName;
            if (rawName && acc?.institutionBranding?.logo) {
              const key = clean(rawName);
              brandingMap[key] = {
                logo: acc.institutionBranding.logo,
                primaryColor: acc.institutionBranding.primaryColor || undefined,
                rawName
              };
            }
          });
          setInstitutionBranding(brandingMap);

          // Create initial connection status from Plaid items as a fallback when sync-all fails
          try {
            const itemsRes = await api.get('/api/plaid/items');
            const statusMap = {};
            (itemsRes.data || []).forEach(item => {
              const name = item?.institutionName;
              if (name) {
                statusMap[name] = { success: item?.status === 'good' };
              }
            });
            setInitialInstitutionStatus(statusMap);
          } catch (itemsErr) {
            // Ignore if unavailable
          }
        } catch (brandingErr) {
          // Non-fatal - silently skip
        }

        // NOTE: We already have partner accounts from the first API call (personalResponse.partnerAccounts)
        // No need to call getAllWallets() separately - it would overwrite our data
        // The partner accounts are already set in the accounts state above (line 459-463)
      } else {
        // If no personal accounts found, try the partner wallet API
        // console.log('🔍 No personal accounts found, trying partner wallet API...');
        const response = await walletService.getAllWallets();
        
        if (response.success) {
          // Extract the separate account types from the response safely
          const safe = response?.data || {};
          const personal = Array.isArray(safe.personal) ? safe.personal : [];
          const joint = Array.isArray(safe.joint) ? safe.joint : [];
          const partner = Array.isArray(safe.partner) ? safe.partner : [];
          const hasPartner = !!safe.hasPartner;
          
          if (DEBUG_WALLET) {
            console.log('🔄 Fallback partner API data received:', {
              personalCount: (personal || []).length,
              jointCount: (joint || []).length,
              partnerCount: (partner || []).length,
              partnerAccounts: partner,
              hasPartner
            });
          }
          
          setAccounts({
            personal,
            joint,
            partner
          });
          
          setHasPartner(hasPartner);
        } else {
          console.error('Failed to fetch wallet accounts:', response.error);
          setError('Failed to fetch accounts');
        }
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Error fetching account data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaidSuccess = async (plaidData) => {
    toast.success(`Connected to ${plaidData.institutionName} successfully!`);
    setShowPlaidLink(false);
    setReconnectItemId(null);
    // Wait a moment for backend to finish processing, then refresh
    setTimeout(async () => {
      await fetchWalletData();
      // Clear any stale sync status from localStorage
      try {
        const institutionName = plaidData.institutionName || 'Bank';
        const existing = JSON.parse(localStorage.getItem('plaidSyncStatus') || '{}');
        if (existing[institutionName]) {
          existing[institutionName] = { success: true, lastSync: new Date().toISOString(), syncType: 'reconnect' };
          localStorage.setItem('plaidSyncStatus', JSON.stringify(existing));
        }
      } catch (e) {
        // noop if storage unavailable
      }
    }, 1500);
  };

  const handleAddAccount = async (formData) => {
    try {
      if (!formData.isFromStatement) {
        const result = await walletService.createAccount(formData);
        
        if (result.success) {
          setShowAddModal(false);
          await fetchWalletData();
          toast.success('Account added successfully');
        } else {
          throw new Error(result.error || 'Failed to add account');
        }
      }
    } catch (error) {
      console.error('❌ Error adding account:', error);
      toast.error(error.message || 'Failed to add account');
    }
  };

  const handleSyncPlaidAccounts = async () => {
    try {
      setLoading(true);
      
      // Batch manual sync via server endpoint with extended timeout
      const batch = await api.post('/api/plaid/sync-all', {}, {
        headers: { 'x-sync-source': 'manual' },
        timeout: 180000
      });
      const results = batch?.data?.results || [];
      
      if (results.length === 0) {
        toast.error('No bank connections found. Connect a bank first.');
        setLoading(false);
        return;
      }
      
      // Track sync status for each institution
      const syncResults = {};
      let hasErrors = false;
      results.forEach(r => {
        const name = r.institutionName || r.itemId || 'Unknown Institution';
        if (r.success) {
          syncResults[name] = { success: true };
        } else {
          hasErrors = true;
          const status = r.status || r.httpStatus;
          const code = r.error_code || r.code;
          const isRateLimited = status === 429 || code === 'RATE_LIMITED';
          syncResults[name] = {
            success: false,
            error: isRateLimited ? 'RATE_LIMITED' : (code === 'ITEM_LOGIN_REQUIRED' ? 'CONNECTION_EXPIRED' : (r.error_type || 'SYNC_ERROR')),
            message: code === 'ITEM_LOGIN_REQUIRED'
              ? `${name} connection expired. Please reconnect.`
              : isRateLimited
                ? `You've hit the manual sync limit. Try again later.`
                : `Failed to sync ${name} accounts.`
          };
          if (code === 'ITEM_LOGIN_REQUIRED') {
            toast.error(`${name} connection expired. Please reconnect your account.`, { duration: 8000 });
          } else if (isRateLimited) {
            toast.error('Sync limit reached. Please wait before syncing again.');
          }
        }
      });
      
      // Store sync results for UI display
      setSyncStatus(syncResults);
      
      // Store sync status in localStorage for persistence (like auto-sync does)
      localStorage.setItem('plaidSyncStatus', JSON.stringify(syncResults));
      
      // Update last sync time  
      localStorage.setItem('lastPlaidSync', Date.now().toString());
      
      // Refresh the account list
      await fetchWalletData();
      
      // Show appropriate success/error message
      if (hasErrors) {
        const successCount = Object.values(syncResults).filter(r => r.success).length;
        const totalCount = Object.keys(syncResults).length;
        const failedInstitutions = Object.entries(syncResults)
          .filter(([_, result]) => !result.success)
          .map(([name, _]) => name);
        
        if (successCount > 0) {
          let message = `${successCount}/${totalCount} accounts synced successfully.`;
          if (failedInstitutions.length > 0) {
            if (failedInstitutions.length <= 3) {
              message += ` ${failedInstitutions.join(', ')} need reconnection.`;
            } else {
              message += ` ${failedInstitutions.length} accounts need reconnection.`;
            }
          }
          toast.success(message, { duration: 8000 });
        } else {
          let message = 'Failed to sync accounts.';
          if (failedInstitutions.length > 0) {
            if (failedInstitutions.length <= 3) {
              message += ` ${failedInstitutions.join(', ')} need reconnection.`;
            } else {
              message += ` ${failedInstitutions.length} accounts need reconnection.`;
            }
          }
          toast.error(message, { duration: 8000 });
        }
      } else {
        toast.success('All accounts synced successfully');
      }
    } catch (error) {
      console.error('Error syncing Plaid accounts:', error);
      toast.error('Failed to sync accounts');
    } finally {
      setLoading(false);
    }
  };

  // Callback for SyncButton success
  const handleSyncSuccess = (syncResults) => {
    try {
      // Merge with existing syncStatus and persist
      const merged = { ...(syncStatus || {}), ...(syncResults || {}) };
      setSyncStatus(merged);
      localStorage.setItem('plaidSyncStatus', JSON.stringify(merged));
      localStorage.setItem('lastPlaidSync', Date.now().toString());
    } catch (_) {}
    // Refresh wallet data after successful sync so balances/cards update
    fetchWalletData();
  };

  // Callback for SyncButton error
  const handleSyncError = (error) => {
    console.error('Sync failed:', error);
  };

  const getAccountTypeDetails = (type) => {
    type = type?.toLowerCase() || '';
    
    const types = {
      checking: {
        color: 'bg-blue-100 text-blue-800',
        icon: BanknotesIcon,
        label: 'Checking'
      },
      savings: { 
        color: 'bg-green-100 text-green-800', 
          icon: BuildingLibraryIcon, 
        label: 'Savings'
      },
      credit: {
        color: 'bg-purple-100 text-purple-800',
        icon: CreditCardIcon,
        label: 'Credit'
      },
      loan: {
        color: 'bg-amber-100 text-amber-800',
        icon: HomeIcon,
        label: 'Loan'
      },
      investment: {
        color: 'bg-emerald-100 text-emerald-800',
        icon: ChartBarIcon,
        label: 'Investment'
      },
      depository: {
        color: 'bg-blue-100 text-blue-800',
          icon: BanknotesIcon, 
        label: 'Depository'
      },
      cash: {
        color: 'bg-green-100 text-green-800',
          icon: CurrencyDollarIcon, 
        label: 'Cash'
      },
      mortgage: {
        color: 'bg-amber-100 text-amber-800',
          icon: HomeIcon, 
        label: 'Mortgage'
      },
      other: {
        color: 'bg-gray-100 text-gray-800',
        icon: CalculatorIcon,
        label: 'Other'
      }
    };
    
    return types[type] || types.other;
  };

  // Get accounts to display based on active tab
  const getFilteredAccounts = () => {
    let displayAccounts = [];
    
    // First filter by view tab (my, our, partner)
    switch (activeViewTab) {
      case 'my':
        // Use only personal accounts (plaidAccounts should already be included)
        displayAccounts = [...(accounts.personal || [])].map(account => ({
          ...account,
          __isOwnedByUser: true
        }));
        break;
      case 'our':
        // Show joint accounts AND personal accounts that are shared with partner AND partner accounts shared with us
        const jointAccounts = (accounts.joint || []).map(account => ({
          ...account,
          __isOwnedByUser: true // Joint accounts are owned by user too
        }));
        
        // Only show shared personal accounts if user actually has an active partner
        const sharedPersonalAccounts = hasPartner && (user?.hasPartner || user?.partner) ? 
          (accounts.personal || []).filter(account => {
            // Check various ways an account can be shared
            return account.sharedWithPartner === true || 
                   account.visibilitySettings?.showToPartner === true ||
                   account.isJointAccount === true ||
                   account.metadata?.sharingSettings?.showToPartner === true;
          }).map(account => ({
            ...account,
            __isOwnedByUser: true,
            __ownerName: user?.firstName || 'You'  // Add owner name for tagging
          })) : [];
        
        const partnerSharedAccounts = (accounts.partner || []).map(account => ({
          ...account,
          __isOwnedByUser: false, // These are accounts the partner has shared with us
          __ownerName: partnerName || user?.partner?.firstName || user?.partnerName || 'Partner'  // Add partner name for tagging
        }));
        
        // Combine all accounts
        let allOurAccounts = [...jointAccounts, ...sharedPersonalAccounts, ...partnerSharedAccounts];
        
        // Deduplicate joint accounts (if an account appears in both user's and partner's lists with matching details)
        const accountMap = new Map();
        const jointAccountPairs = [];
        
        allOurAccounts.forEach(account => {
          const key = `${account.bankName || account.institutionName}-${account.lastFourDigits || account.mask}-${account.accountType || account.type}`.toLowerCase().replace(/\s/g, '');
          
          if (accountMap.has(key)) {
            // Found a duplicate - this is a joint account
            const existingAccount = accountMap.get(key);
            
            // Mark both as joint and only keep one
            existingAccount.__isJoint = true;
            account.__isJoint = true;
            
            // Keep the user's version if one exists, otherwise keep the first one
            if (account.__isOwnedByUser && !existingAccount.__isOwnedByUser) {
              accountMap.set(key, { ...account, __isJoint: true });
            } else if (!existingAccount.__isOwnedByUser && !account.__isOwnedByUser) {
              // Both are partner accounts (shouldn't happen but handle it)
              accountMap.set(key, { ...existingAccount, __isJoint: true });
            }
            // If existingAccount is owned by user, keep it
            
            jointAccountPairs.push({ key, accounts: [existingAccount, account] });
          } else {
            // Check if this account is marked as joint in the database
            if (account.isJointAccount || account.metadata?.isJointAccount) {
              account.__isJoint = true;
            }
            accountMap.set(key, account);
          }
        });
        
        displayAccounts = Array.from(accountMap.values());
        
        if (DEBUG_WALLET) {
          console.log('🔍 Our Wallet filtering:', {
            hasPartner: hasPartner,
            sharedPersonal: sharedPersonalAccounts.length,
            partnerShared: partnerSharedAccounts.length,
            beforeDedup: allOurAccounts.length,
            afterDedup: displayAccounts.length,
            jointPairsFound: jointAccountPairs.length,
            totalOurWallet: displayAccounts.length
          });
        }
        break;
      case 'partner':
        // Show partner accounts
        displayAccounts = (accounts.partner || []).map(account => ({
          ...account,
          __isOwnedByUser: false
        }));
        break;
      default:
        // Default to personal accounts (plaidAccounts should already be included)
        displayAccounts = [...(accounts.personal || [])].map(account => ({
          ...account,
          __isOwnedByUser: true
        }));
        break;
    }
    
    if (DEBUG_WALLET) {
      console.log('🔍 Display accounts before filtering:', displayAccounts.length);
    }
    
    // Then filter by account type
    if (activeTab !== 'all') {
      const grouped = getGroupedAccounts(displayAccounts);
      if (DEBUG_WALLET) {
        console.log('🔍 Active filter tab:', activeTab);
      }
      
      switch (activeTab) {
        case 'checking':
          return grouped.checking;
        case 'savings':
          return grouped.savings;
        case 'credit':
          return grouped.credit;
        case 'loans':
          return grouped.loan;
        case 'investments':
          return grouped.investment;
        default:
          return displayAccounts;
      }
    }
    
    return displayAccounts;
  };

    // Group accounts by type
  const getGroupedAccounts = (accountsList) => {
    const groupedAccounts = {
      checking: [],
      savings: [],
      credit: [],
      loan: [],
      investment: [],
      depository: [],
      other: []
    };
    
    accountsList.forEach((account, index) => {
      // Use enhanced account type detection (consistent with other functions)
      const accountType = account.accountType?.toLowerCase() || account.type?.toLowerCase() || '';
      const accountSubtype = account.subtype?.toLowerCase() || '';
      
      // Enhanced credit card detection (consistent with getFinancialSummary and WalletCard)
      const isCreditCard = (
        account.accountType === 'credit' || 
        account.accountType === 'credit card' ||
        account.type === 'credit' ||
        account.subtype === 'credit card' ||
        account.subtype === 'credit' ||
        (account.name && account.name.toLowerCase().includes('credit')) ||
        (account.name && account.name.toLowerCase().includes('card')) ||
        (account.name && account.name.toLowerCase().includes('visa')) ||
        (account.name && account.name.toLowerCase().includes('mastercard')) ||
        (account.name && account.name.toLowerCase().includes('amex')) ||
        (account.name && account.name.toLowerCase().includes('american express')) ||
        (account.name && account.name.toLowerCase().includes('costco')) ||
        (account.name && account.name.toLowerCase().includes('discover'))
      );
      
      if (DEBUG_WALLET) {
        console.log(`🏷️ Grouping account ${index + 1}: ${account.name || account.bankName}`);
      }
      
      // Enhanced credit card balance calculation with Chase anomaly detection
      let balance;
      if (isCreditCard) {
        try {
          console.log(`💳 CREDIT CARD RAW DATA:`, {
            currentBalance: account.currentBalance,
            availableBalance: account.availableBalance,
            balance: account.balance,
            type: account.type,
            subtype: account.subtype,
            mask: account.mask
          });
          
          // **CHASE ANOMALY DETECTION & FIX**
          // Chase sometimes reports currentBalance = availableBalance when card is actually paid off
          const hasChaseAnomaly = (
            account.currentBalance === account.availableBalance && 
            account.currentBalance > 0 &&
            account.currentBalance >= 1000 // Likely credit limit, not debt
          );
          
          if (hasChaseAnomaly) {
            
            // For Chase anomalies, assume card is paid off
            balance = 0;
          } else {
            // Normal credit card logic - use currentBalance as debt amount
            balance = account.currentBalance || 0;
          }
          
        } catch (error) {
          console.error('Error in credit card balance calculation:', error);
          // Fallback to simple logic if there's any error
          balance = account.currentBalance || 0;
        }
      } else {
        // For non-credit accounts, use the standard fallback chain
        balance = account.currentBalance || account.availableBalance || account.balance || 0;
      }
      
      let creditLimit = account.creditLimit || account.limit || 0;
      
      // For credit cards, be more aggressive about finding the credit limit
      let actualCreditLimit = creditLimit;
      if (isCreditCard) {
        // Try all possible credit limit fields
        actualCreditLimit = account.creditLimit || account.limit || 0;
      
        // If still no limit found, check if availableBalance might be the available credit
        if (actualCreditLimit === 0 && account.availableBalance && account.availableBalance > 0) {
          // Pattern 1: availableBalance = remaining credit limit
          const potentialLimit = account.availableBalance + Math.abs(balance);
          actualCreditLimit = potentialLimit;
        }
        
        // If still no limit, check other possible patterns
        if (actualCreditLimit === 0) {
          // Look for other possible credit limit fields
          const possibleFields = [
            account.balances?.limit,
            account.balances?.available + Math.abs(balance),
            account.metadata?.creditLimit,
            account.creditLimitAmount
          ].filter(val => val && val > 0);
          
          if (possibleFields.length > 0) {
            actualCreditLimit = possibleFields[0];
            console.log(`🔍 Found credit limit in alternate field: ${actualCreditLimit}`);
          }
        }
        
        // For testing: If this is the Costco card and we still don't have a limit, use a mock value
        if (actualCreditLimit === 0 && account.name && account.name.toLowerCase().includes('costco')) {
          actualCreditLimit = 5000; // Mock credit limit for testing
          console.log(`💳 Using mock credit limit for Costco card in financial summary: ${actualCreditLimit}`);
        }
        
        // Last resort: If WalletCard shows a utilization %, reverse engineer the limit
        if (actualCreditLimit === 0) {
          // This is a temporary hack - we should find the real data source
          console.log(`🔍 No credit limit found for ${account.name}, will rely on individual card calculation`);
        }
      }
      
      
      if (accountType === 'depository' && accountSubtype === 'checking') {
        groupedAccounts.checking.push(account);
      } else if (accountType === 'depository' && accountSubtype === 'savings') {
        groupedAccounts.savings.push(account);
      } else if (isCreditCard) {
        const creditBalance = Math.abs(balance);
        groupedAccounts.credit.push(account);
      } else if (accountType === 'loan' || accountSubtype === 'loan') {
        groupedAccounts.loan.push(account);
      } else if (accountType === 'investment') {
        groupedAccounts.investment.push(account);
      } else if (accountType === 'depository') {
        groupedAccounts.depository.push(account);
      } else if (accountType === 'checking') {
        // Handle manual accounts with simple types
        groupedAccounts.checking.push(account);
      } else if (accountType === 'savings') {
        groupedAccounts.savings.push(account);
      } else {
        groupedAccounts.other.push(account);
      }
    });
    
    console.log('🏷️ Final grouped accounts:', {
      checking: groupedAccounts.checking.length,
      savings: groupedAccounts.savings.length,
      credit: groupedAccounts.credit.length,
      loan: groupedAccounts.loan.length,
      investment: groupedAccounts.investment.length,
      depository: groupedAccounts.depository.length,
      other: groupedAccounts.other.length
    });
    
    return groupedAccounts;
  };

  // Calculate financial summary for accounts
  const getFinancialSummary = (accountsList) => {
    console.log('📊 Calculating financial summary for accounts:', accountsList);
    
    // 🔄 DEDUPLICATE JOINT ACCOUNTS
    // When viewing "Our Wallet", both partners have copies of the same joint accounts
    // We need to count each joint account only ONCE to avoid double-counting
    const seenJointAccounts = new Set();
    const deduplicatedAccounts = accountsList.filter((account) => {
      // Check if this is a joint/shared account
      const isJoint = account.isJointAccount || account.sharedWithPartner || account.ownershipType === 'joint';
      
      if (isJoint && account.mask) {
        // Create a unique key for this account (institution + mask)
        const institutionName = account.itemId?.institutionName || account.institutionName || account.bankName || '';
        const accountKey = `${institutionName}-${account.mask}`;
        
        if (seenJointAccounts.has(accountKey)) {
          // Skip this duplicate joint account
          console.log(`🔄 Skipping duplicate joint account: ${account.name} (${account.mask})`);
          return false;
        }
        
        // Mark this joint account as seen
        seenJointAccounts.add(accountKey);
      }
      
      return true; // Include this account
    });
    
    console.log(`📊 Deduplicated accounts: ${accountsList.length} → ${deduplicatedAccounts.length} (removed ${accountsList.length - deduplicatedAccounts.length} duplicates)`);
    
    let checking = 0;
    let savings = 0;
    let credit = 0;
    let loans = 0;
    let investments = 0;
    let totalAssets = 0;
    let totalDebtFull = 0; // ALWAYS includes all debt (for accurate Net Worth)
    let totalMortgageDebt = 0; // Track mortgages separately
    let totalMortgageAssets = 0; // Track mortgage/home asset values separately
    let totalAvailableCredit = 0;
    let totalCreditUsed = 0;

    // Track credit cards specifically for debugging
    const creditCards = [];

    deduplicatedAccounts.forEach((account, index) => {
      const accountType = account.accountType?.toLowerCase() || account.type?.toLowerCase() || '';
      const accountSubtype = account.subtype?.toLowerCase() || '';
      
      // Enhanced credit card detection (consistent with WalletCard)
      const isCreditCard = (
        account.accountType === 'credit' || 
        account.accountType === 'credit card' ||
        account.type === 'credit' ||
        account.subtype === 'credit card' ||
        account.subtype === 'credit' ||
        (account.name && account.name.toLowerCase().includes('credit')) ||
        (account.name && account.name.toLowerCase().includes('card')) ||
        (account.name && account.name.toLowerCase().includes('visa')) ||
        (account.name && account.name.toLowerCase().includes('mastercard')) ||
        (account.name && account.name.toLowerCase().includes('amex')) ||
        (account.name && account.name.toLowerCase().includes('american express')) ||
        (account.name && account.name.toLowerCase().includes('costco')) ||
        (account.name && account.name.toLowerCase().includes('discover'))
      );
      
      // Enhanced credit card balance calculation with Chase anomaly detection
      let balance;
      if (isCreditCard) {
        try {
          console.log(`💳 CREDIT CARD RAW DATA:`, {
            currentBalance: account.currentBalance,
            availableBalance: account.availableBalance,
            balance: account.balance,
            type: account.type,
            subtype: account.subtype,
            mask: account.mask
          });
          
          // **CHASE ANOMALY DETECTION & FIX**
          // Chase sometimes reports currentBalance = availableBalance when card is actually paid off
          const hasChaseAnomaly = (
            account.currentBalance === account.availableBalance && 
            account.currentBalance > 0 &&
            account.currentBalance >= 1000 // Likely credit limit, not debt
          );
          
          if (hasChaseAnomaly) {
            
            // For Chase anomalies, assume card is paid off
            balance = 0;
          } else {
            // Normal credit card logic - use currentBalance as debt amount
            balance = account.currentBalance || 0;
          }
          
        } catch (error) {
          console.error('Error in credit card balance calculation:', error);
          // Fallback to simple logic if there's any error
          balance = account.currentBalance || 0;
        }
      } else {
        // For non-credit accounts, use the standard fallback chain
        balance = account.currentBalance || account.availableBalance || account.balance || 0;
      }
      
      let creditLimit = account.creditLimit || account.limit || 0;
      
      // For credit cards, be more aggressive about finding the credit limit
      let actualCreditLimit = creditLimit;
      if (isCreditCard) {
        // Try all possible credit limit fields
        actualCreditLimit = account.creditLimit || account.limit || 0;
      
        // If still no limit found, check if availableBalance might be the available credit
        if (actualCreditLimit === 0 && account.availableBalance && account.availableBalance > 0) {
          // Pattern 1: availableBalance = remaining credit limit
          const potentialLimit = account.availableBalance + Math.abs(balance);
          actualCreditLimit = potentialLimit;
        }
        
        // If still no limit, check other possible patterns
        if (actualCreditLimit === 0) {
          // Look for other possible credit limit fields
          const possibleFields = [
            account.balances?.limit,
            account.balances?.available + Math.abs(balance),
            account.metadata?.creditLimit,
            account.creditLimitAmount
          ].filter(val => val && val > 0);
          
          if (possibleFields.length > 0) {
            actualCreditLimit = possibleFields[0];
            console.log(`🔍 Found credit limit in alternate field: ${actualCreditLimit}`);
          }
        }
        
        // For testing: If this is the Costco card and we still don't have a limit, use a mock value
        if (actualCreditLimit === 0 && account.name && account.name.toLowerCase().includes('costco')) {
          actualCreditLimit = 5000; // Mock credit limit for testing
          console.log(`💳 Using mock credit limit for Costco card in financial summary: ${actualCreditLimit}`);
        }
        
        // Last resort: If WalletCard shows a utilization %, reverse engineer the limit
        if (actualCreditLimit === 0) {
          // This is a temporary hack - we should find the real data source
          console.log(`🔍 No credit limit found for ${account.name}, will rely on individual card calculation`);
        }
      }
      
      
      if (accountType === 'depository' && accountSubtype === 'checking') {
        checking += balance;
        totalAssets += balance;
      } else if (accountType === 'depository' && accountSubtype === 'savings') {
        savings += balance;
        totalAssets += balance;
      } else if (isCreditCard) {
        const creditBalance = Math.abs(balance);
        credit += creditBalance;
        totalDebtFull += creditBalance; // Credit cards always count toward total debt
        totalCreditUsed += creditBalance;
        
        // Track this credit card for debugging
        const cardInfo = {
          name: account.name || account.bankName,
          balance: creditBalance,
          limit: actualCreditLimit,
          hasLimit: actualCreditLimit > 0
        };
        
        // Only add to available credit if we have a valid limit
        if (actualCreditLimit > 0) {
          totalAvailableCredit += actualCreditLimit;
          cardInfo.utilization = (creditBalance / actualCreditLimit * 100).toFixed(1);
          console.log(`💳 Credit card ${account.name}: Using ${creditBalance} of ${actualCreditLimit} limit (${cardInfo.utilization}%)`);
        } else {
          // For credit cards without limits, don't estimate - show as unknown
          console.log(`⚠️ Credit card ${account.name} has no detectable limit - excluding from utilization calculation`);
          console.log(`   This may cause discrepancy with individual card display`);
          cardInfo.utilization = 'Unknown (no limit data)';
          // Don't add anything to totalAvailableCredit - this will show 0% if this is the only card
        }
        
        creditCards.push(cardInfo);
      } else if (accountType === 'loan' || accountSubtype === 'loan' || 
                 accountType === 'mortgage' || accountSubtype === 'mortgage' ||
                 accountType === 'auto' || accountSubtype === 'auto' ||
                 accountType === 'loans & lines' || account.type === 'loan') {
        
        // 🚫 SKIP INCOMPLETE LOANS - Don't include in financial calculations until setup is complete
        const loanIsComplete = isLoanComplete(account);
        if (!loanIsComplete) {
          console.log(`⚠️ Skipping incomplete loan ${account.name} from financial calculations`);
          return; // Skip this account entirely (return in forEach = continue)
        }
        
        // For loans (mortgages, auto loans), handle properly based on ownership type
        const loanBalance = Math.abs(balance); // Amount owed (debt)
        const ownershipType = account.loanDetails?.ownershipType || account.loanOwnershipType;
        
        // NEW: Use proper asset value fields for equity calculation
        // Priority: currentValue (market value) > purchasePrice > originalPrincipal (fallback)
        const currentValue = account.loanDetails?.currentValue || account.currentValue;
        const purchasePrice = account.loanDetails?.purchasePrice || account.purchasePrice;
        const originalPrincipal = account.loanDetails?.originalPrincipal || account.originalAmount;
        const originalValue = currentValue || purchasePrice || originalPrincipal;
        
        // Check if this is a mortgage
        const isMortgage = accountType === 'mortgage' || accountSubtype === 'mortgage' || 
                          (account.name && account.name.toLowerCase().includes('mortgage'));
        
        // Handle different ownership types
        if (ownershipType === 'lease') {
          // LEASE: You don't own it, so NO asset value, only debt (monthly payments)
          // Net Worth Impact: Negative (just the debt)
          totalDebtFull += loanBalance; // ALWAYS count for Net Worth accuracy
          if (isMortgage) {
            totalMortgageDebt += loanBalance; // Track separately for toggle
          }
          loans += loanBalance;
          console.log(`📝 Lease ${account.name}:`, {
            loanBalance: loanBalance,
            netWorthContribution: -loanBalance,
            note: 'Lease - no asset value, only debt',
            isMortgage: isMortgage
          });
        } else if (ownershipType === 'owned') {
          // OWNED OUTRIGHT: You own it, no debt
          // Net Worth Impact: Positive (asset value only)
          if (originalValue && originalValue > 0) {
            totalAssets += originalValue;
            console.log(`✅ Owned ${account.name}:`, {
              assetValue: originalValue,
              valueSource: currentValue ? 'currentValue' : (purchasePrice ? 'purchasePrice' : 'originalPrincipal'),
              currentMarketValue: currentValue,
              purchasePrice: purchasePrice,
              netWorthContribution: originalValue,
              note: 'Owned outright - no debt'
            });
          }
        } else {
          // FINANCED or default loan behavior: You own it but owe money
          // Net Worth Impact: Asset Value - Loan Balance = Equity
          loans += loanBalance;
          totalDebtFull += loanBalance; // ALWAYS count for Net Worth accuracy
          if (isMortgage) {
            totalMortgageDebt += loanBalance; // Track separately for toggle
          }
          
          if (originalValue && originalValue > 0) {
            // We have the asset value (current market value, purchase price, or original loan amount)
            totalAssets += originalValue;
            
            // Track mortgage/home asset values for net worth toggle
            if (isMortgage) {
              totalMortgageAssets += originalValue;
            }
            
            // Calculate equity for logging
            const equity = originalValue - loanBalance;
            console.log(`🏠 ${ownershipType === 'financed' ? 'Financed' : 'Loan'} ${account.name}:`, {
              assetValue: originalValue,
              valueSource: currentValue ? 'currentValue' : (purchasePrice ? 'purchasePrice' : 'originalPrincipal'),
              currentMarketValue: currentValue,
              purchasePrice: purchasePrice,
              originalLoanAmount: originalPrincipal,
              loanBalance: loanBalance,
              calculatedEquity: equity,
              netWorthContribution: equity,
              isMortgage: isMortgage
            });
          } else {
            // No original value - check if we have equity stored
            const storedEquity = account.loanDetails?.equity || account.equity;
            
            if (storedEquity && storedEquity > 0) {
              // Use stored equity directly
              // Equity = Current Property Value - Loan Balance
              // So: Current Property Value = Equity + Loan Balance
              const impliedPropertyValue = storedEquity + loanBalance;
              totalAssets += impliedPropertyValue;
              
              // Track mortgage/home asset values for net worth toggle
              if (isMortgage) {
                totalMortgageAssets += impliedPropertyValue;
              }
              
              console.log(`🏠 Loan ${account.name} (using stored equity):`, {
                storedEquity: storedEquity,
                loanBalance: loanBalance,
                impliedPropertyValue: impliedPropertyValue,
                netWorthContribution: storedEquity,
                isMortgage: isMortgage
              });
            } else {
              // No original value and no equity - can't determine asset value
              // Just count the debt, don't add any asset value
              console.log(`⚠️ Loan ${account.name}: No original value or equity found. Only counting debt of ${loanBalance}`, {
                isMortgage: isMortgage
              });
            }
          }
        }
      } else if (accountType === 'investment') {
        investments += balance;
        totalAssets += balance;
      } else if (accountType === 'depository') {
        totalAssets += balance;
      } else if (accountType === 'checking') {
        // Handle manual accounts with simple types
        checking += balance;
        totalAssets += balance;
      } else if (accountType === 'savings') {
        savings += balance;
        totalAssets += balance;
      } else {
        // Default to assets for unknown types with positive balance
        if (balance > 0) {
          totalAssets += balance;
        }
      }
    });

    const creditUtilization = totalAvailableCredit > 0 ? (totalCreditUsed / totalAvailableCredit) * 100 : 0;

    // Calculate display debt based on toggle (for Total Debt card only)
    const totalDebtDisplay = includeMortgagesInDebt ? totalDebtFull : (totalDebtFull - totalMortgageDebt);

    // Calculate net worth with and without mortgages
    const netWorthFull = totalAssets - totalDebtFull; // Full net worth (includes home equity)
    // FIX: When excluding mortgages, subtract BOTH the home asset value AND the mortgage debt
    const netWorthWithoutMortgages = (totalAssets - totalMortgageAssets) - (totalDebtFull - totalMortgageDebt); // Liquid net worth (excludes home & mortgage entirely)
    const netWorthDisplay = includeMortgagesInNetWorth ? netWorthFull : netWorthWithoutMortgages;

    const result = {
      checking,
      savings,
      credit,
      loans,
      investments,
      totalAssets,
      totalDebt: totalDebtDisplay, // For display in Total Debt card
      totalDebtFull, // Full debt including mortgages (always)
      totalMortgageDebt, // Mortgages only
      totalAvailableCredit,
      totalCreditUsed,
      netWorth: netWorthDisplay, // Display net worth based on toggle
      netWorthFull, // Full net worth (always)
      netWorthWithoutMortgages, // Net worth excluding mortgages
      creditUtilization
    };
    
    console.log('💰 Financial Summary Result:', result);
    console.log(`💳 Credit Utilization: ${totalCreditUsed} / ${totalAvailableCredit} = ${creditUtilization.toFixed(1)}%`);
    console.log(`📊 Credit Cards Found:`, creditCards);
    console.log(`🔍 Credit breakdown: Used=${totalCreditUsed}, Available=${totalAvailableCredit}, Utilization=${creditUtilization.toFixed(1)}%`);
    console.log(`🏠 Mortgage Toggle: includeMortgagesInDebt=${includeMortgagesInDebt}, totalMortgageDebt=${totalMortgageDebt}, totalDebtDisplay=${totalDebtDisplay}, totalDebtFull=${totalDebtFull}`);
    console.log(`🏡 Net Worth Toggle: includeMortgagesInNetWorth=${includeMortgagesInNetWorth}, totalMortgageAssets=${totalMortgageAssets}, totalMortgageDebt=${totalMortgageDebt}, netWorthFull=${netWorthFull}, netWorthWithoutMortgages=${netWorthWithoutMortgages}, netWorthDisplay=${netWorthDisplay}`);
    
    // Alert if there's a significant discrepancy
    if (creditCards.length === 1 && creditCards[0].hasLimit) {
      const individualUtilization = parseFloat(creditCards[0].utilization);
      const overallUtilization = creditUtilization;
      const difference = Math.abs(individualUtilization - overallUtilization);
      
      if (difference > 1) {
        console.warn(`⚠️ UTILIZATION DISCREPANCY: Individual card shows ${individualUtilization}% but overall shows ${overallUtilization.toFixed(1)}%`);
      }
    }
    
    return result;
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format currency for debt display (negative values)
  const formatDebt = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(-Math.abs(amount)); // Always show as negative
  };

  // Calculate if a number is positive
  const isPositive = (num) => num >= 0;

  // Get filtered accounts to display
  const filteredAccounts = getFilteredAccounts();
  
  // Get accounts for financial summary based on active view tab
  // This makes the summary cards (Total Debt, Net Worth, Credit Utilization) reflect the selected wallet
  const summaryAccounts = (() => {
    switch (activeViewTab) {
      case 'my':
        return [...(accounts.personal || [])];
      case 'our':
        // Show combined: joint + shared personal + partner shared
        const jointAccounts = accounts.joint || [];
        const sharedPersonalAccounts = hasPartner && (user?.hasPartner || user?.partner) ? 
          (accounts.personal || []).filter(account => {
            return account.sharedWithPartner === true || 
                   account.visibilitySettings?.showToPartner === true ||
                   account.isJointAccount === true ||
                   account.metadata?.sharingSettings?.showToPartner === true;
          }) : [];
        const partnerSharedAccounts = accounts.partner || [];
        return [...jointAccounts, ...sharedPersonalAccounts, ...partnerSharedAccounts];
      case 'partner':
        return [...(accounts.partner || [])];
      default:
        return [...(accounts.personal || [])];
    }
  })();
  
  // Get all accounts for debugging (regardless of current view/filter)
  const allAccounts = [...(accounts.personal || []), ...(accounts.joint || []), ...(accounts.partner || [])];
  
  // Debug: Check for potential duplicates
  console.log('🔍 Account Arrays Debug:', {
    personalCount: (accounts.personal || []).length,
    jointCount: (accounts.joint || []).length,
    partnerCount: (accounts.partner || []).length,
    plaidAccountsCount: (plaidAccounts || []).length,
    totalAllAccounts: allAccounts.length,
    summaryAccountsCount: summaryAccounts.length,
    activeViewTab: activeViewTab,
    personalAccounts: accounts.personal?.map(acc => ({ name: acc.name || acc.bankName, id: acc._id, isPlaid: acc.isPlaidAccount })),
    plaidAccounts: plaidAccounts?.map(acc => ({ name: acc.name || acc.bankName, id: acc._id, isPlaid: acc.isPlaidAccount }))
  });
  
  // Get financial summary for the active view tab (for the top summary cards)
  const overallSummary = getFinancialSummary(summaryAccounts);
  
  // Get financial summary for current filtered view (for display context)
  const filteredSummary = getFinancialSummary(filteredAccounts);
  
  // Get partner name for display - use state variable or fallback
  const displayPartnerName = partnerName || (() => {
    if (user?.partner && typeof user.partner === 'object') {
      return user.partner.firstName || 'Partner';
    }
    if (user?.createdPartner && typeof user.createdPartner === 'object') {
      return user.createdPartner.firstName || 'Partner';
    }
    return 'Partner';
  })();

  // Update the handleMarkAsJoint function
  const handleMarkAsJoint = async (accountId) => {
    try {
      setActionInProgress(true);
      const response = await walletService.markAccountAsJoint(accountId);
      
      if (response.success) {
        // Update local state to reflect the change
        setAccounts(prevAccounts => {
          // Find the account that was marked as joint
          const accountIndex = prevAccounts.personal.findIndex(acc => acc._id === accountId);
          
          if (accountIndex !== -1) {
            // Move the account from personal to joint
            const updatedAccount = { 
              ...prevAccounts.personal[accountIndex],
              ownershipType: 'joint',
              isJointAccount: true
            };
            
            const newPersonal = [...prevAccounts.personal];
            newPersonal.splice(accountIndex, 1);
            
            return {
              ...prevAccounts,
              personal: newPersonal,
              joint: [...prevAccounts.joint, updatedAccount]
            };
          }
          
          return prevAccounts;
        });
        
        toast.success(response.message || 'Account marked as joint successfully');
      } else {
        console.error('Failed to mark account as joint:', response.error);
        toast.error(response.error || 'Failed to mark account as joint');
      }
    } catch (err) {
      console.error('Error marking account as joint:', err);
      toast.error('Error marking account as joint');
    } finally {
      setActionInProgress(false);
    }
  };

  // Add a function to update sharing settings
  const handleUpdateSharingSettings = async (accountId, settings) => {
    try {
      setActionInProgress(true);
      const response = await walletService.updateSharingSettings(accountId, settings);
      
      if (response.success) {
        // Update local state to reflect the change
        setAccounts(prevAccounts => {
          // Find the account in the personal accounts
          const accountIndex = prevAccounts.personal.findIndex(acc => acc._id === accountId);
          
          if (accountIndex !== -1) {
            // Update the account with new settings
            const updatedAccount = { 
              ...prevAccounts.personal[accountIndex],
              visibilitySettings: {
                ...prevAccounts.personal[accountIndex].visibilitySettings,
                ...settings
              },
              sharedWithPartner: settings.showToPartner !== undefined 
                ? settings.showToPartner 
                : prevAccounts.personal[accountIndex].sharedWithPartner
            };
            
            const newPersonal = [...prevAccounts.personal];
            newPersonal[accountIndex] = updatedAccount;
            
            return {
              ...prevAccounts,
              personal: newPersonal
            };
          }
          
          return prevAccounts;
        });
        
        toast.success(response.message || 'Account sharing settings updated successfully');
      } else {
        console.error('Failed to update sharing settings:', response.error);
        toast.error(response.error || 'Failed to update sharing settings');
      }
    } catch (err) {
      console.error('Error updating sharing settings:', err);
      toast.error('Error updating sharing settings');
    } finally {
      setActionInProgress(false);
    }
  };

  // Handle sending partner invitation
  const handleSendInvitation = async () => {
    if (!invitationEmail) {
      toast.error('Please enter an email address');
      return;
    }

    if (!invitationEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setSendingInvitation(true);
      const response = await generatePartnerInvite(invitationEmail);
      
      const isSuccess = response && 
                       (response.success || 
                        response.message?.toLowerCase().includes('success') ||
                        response.status === 'success' ||
                        response.status === 200);
      
      if (isSuccess) {
        toast.success(`Invitation sent to ${invitationEmail}`);
        setInvitationEmail('');
        // setShowInvitationModal(false); // This modal is handled by the shared component
        // Refresh invitations to show the newly sent invitation
        await fetchInvitations();
      } else {
        toast.error(response?.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setSendingInvitation(false);
    }
  };

  // Handle accepting a partner invitation
  const handleAcceptInvitation = async (invitationId) => {
    try {
      await acceptPartnerInvite(invitationId, false);
      toast.success('Invitation accepted successfully');
      // Refresh both user data and invitations
      await refreshUserData();
      await fetchInvitations();
      await fetchWalletData(); // Refresh wallet data to get partner accounts
    } catch (error) {
      console.error('Failed to accept invitation', error);
      
      // Check if the error indicates the invitation was already processed
      if (error.response?.data?.message?.includes('not found') || 
          error.response?.data?.message?.includes('already been processed')) {
        toast.error('This invitation has already been processed. Please refresh the page.');
        // Refresh data to get current state
        await refreshUserData();
        await fetchInvitations();
      } else {
        toast.error('Failed to accept invitation');
      }
    }
  };

  // Handle rejecting a partner invitation
  const handleRejectInvitation = async (invitationId) => {
    try {
      await rejectPartnerInvite(invitationId);
      toast.success('Invitation rejected');
      await fetchInvitations();
    } catch (error) {
      console.error('Failed to reject invitation', error);
      toast.error('Failed to reject invitation');
    }
  };

  // Handle cancelling a partner invitation
  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelPartnerInvite(invitationId);
      toast.success('Invitation cancelled successfully');
      await fetchInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation', error);
      toast.error('Failed to cancel invitation');
    }
  };

  // Handle resending a partner invitation
  const handleResendInvitation = async (invitationId) => {
    try {
      // Find the invitation to get the email
      const invitation = sentInvitations.find(inv => inv._id === invitationId);
      if (!invitation) {
        toast.error('Invitation not found');
        return;
      }

      // Check if the invitation is in a valid state for resending
      if (invitation.status !== 'PENDING') {
        toast.warning('Only pending invitations can be resent');
        return;
      }
      
      // For now, since resend endpoint is not available on production,
      // we'll just send a new invitation to the same email
      const response = await generatePartnerInvite(invitation.recipientEmail);
      
      const isSuccess = response && 
                       (response.success || 
                        response.message?.toLowerCase().includes('success') ||
                        response.status === 'success' ||
                        response.status === 200);
      
      if (isSuccess) {
        toast.success(`New invitation sent to ${invitation.recipientEmail}`);
      await fetchInvitations();
      } else {
        toast.error(response?.message || 'Failed to send new invitation');
      }
    } catch (error) {
      console.error('Failed to send new invitation:', error);
      
      // Check if it's a 404 error (endpoint not found)
      if (error.response?.status === 404) {
        toast.error('Resend feature not available. Please send a new invitation instead.');
      } else {
        toast.error(error.message || 'Failed to resend invitation');
      }
    }
  };

  // Group accounts by institution
  const getAccountsByInstitution = (accountsList) => {
    const grouped = {};
    
    accountsList.forEach(account => {
      // Determine the institution name
      let institutionName = 'Other';
      
      if (account.isPlaidAccount) {
        // For Plaid accounts, use institutionName or bankName
        institutionName = account.institutionName || account.bankName || 'Connected Bank';
      } else {
        // For manual accounts, use bankName
        institutionName = account.bankName || 'Manual Account';
      }
      
      // Initialize the institution group if it doesn't exist
      if (!grouped[institutionName]) {
        grouped[institutionName] = [];
      }
      
      // Add the account to the institution group
      grouped[institutionName].push(account);
    });
    
    return grouped;
  };

  // Calculate financial summary for a specific institution
  const getInstitutionSummary = (accounts) => {
    let totalBalance = 0;
    let totalDebt = 0;
    
    // Determine institution name for debugging
    const institutionName = accounts.length > 0 ? (accounts[0].institutionName || accounts[0].bankName || 'Unknown') : 'Empty';
    
    console.log(`🏛️ ${institutionName} Institution Summary - Processing ${accounts.length} accounts:`, accounts.map(acc => {
      const isCreditCard = (
        acc.accountType === 'credit' || 
        acc.accountType === 'credit card' ||
        acc.type === 'credit' ||
        acc.subtype === 'credit card' ||
        acc.subtype === 'credit' ||
        (acc.name && acc.name.toLowerCase().includes('credit')) ||
        (acc.name && acc.name.toLowerCase().includes('card'))
      );
      
      return {
        name: acc.name || acc.bankName,
        currentBalance: acc.currentBalance || 0,
        availableBalance: acc.balance || 0,
        calculatedBalance: acc.currentBalance || acc.balance || 0,
        accountType: acc.accountType,
        type: acc.type,
        subtype: acc.subtype,
        isCreditCard
      };
    }));
    
    accounts.forEach(account => {
      const accountType = account.accountType?.toLowerCase() || account.type?.toLowerCase() || '';
      const accountSubtype = account.subtype?.toLowerCase() || '';
      
      // Enhanced credit card detection (same as other functions)
      const isCreditCard = (
        account.accountType === 'credit' || 
        account.accountType === 'credit card' ||
        account.type === 'credit' ||
        account.subtype === 'credit card' ||
        account.subtype === 'credit' ||
        (account.name && account.name.toLowerCase().includes('credit')) ||
        (account.name && account.name.toLowerCase().includes('card')) ||
        (account.name && account.name.toLowerCase().includes('visa')) ||
        (account.name && account.name.toLowerCase().includes('mastercard')) ||
        (account.name && account.name.toLowerCase().includes('amex')) ||
        (account.name && account.name.toLowerCase().includes('american express')) ||
        (account.name && account.name.toLowerCase().includes('costco')) ||
        (account.name && account.name.toLowerCase().includes('discover'))
      );
      
      // **CRITICAL FIX**: For credit cards, use currentBalance (debt) not balance (credit limit)
      let effectiveBalance;
      if (isCreditCard) {
        // For credit cards: currentBalance = debt owed, balance = available credit
        effectiveBalance = account.currentBalance || 0;
        
        // **CHASE ANOMALY DETECTION & FIX** (same as UI logic)
        const hasChaseAnomaly = (
          account.currentBalance === account.availableBalance && 
          account.currentBalance > 0 &&
          account.currentBalance >= 1000 // Likely credit limit, not debt
        );
        
        if (hasChaseAnomaly) {
          console.log(`🔧 CHASE ANOMALY in institution summary:`, {
            account: account.name,
            reportedDebt: account.currentBalance,
            availableCredit: account.availableBalance,
            action: 'Treating as $0 debt'
          });
          effectiveBalance = 0;
        }
      } else {
        // For non-credit accounts: use standard fallback
        effectiveBalance = account.currentBalance || account.balance || 0;
      }
      
      console.log(`🏛️ Processing ${account.name || account.bankName}:`, {
        currentBalance: account.currentBalance,
        availableBalance: account.balance,
        effectiveBalance,
        accountType,
        subtype: accountSubtype,
        isCreditCard,
        willBeDebt: isCreditCard || accountType === 'loan' || accountSubtype === 'loan'
      });
      
      const isLoan = accountType === 'loan' || accountSubtype === 'loan' ||
        (account.name && (
          account.name.toLowerCase().includes('loan') ||
          account.name.toLowerCase().includes('mortgage') ||
          account.name.toLowerCase().includes('auto')
        ));

      if (isCreditCard) {
        // Credit card debt reduces net
        const debtAmount = Math.abs(effectiveBalance);
        totalDebt += debtAmount;
        console.log(`   → Added ${debtAmount} to DEBT (total debt now: ${totalDebt})`);
      } else if (isLoan) {
        // Loans are excluded from institution-level Net (they are long-term liabilities listed separately)
        console.log(`   → LOAN excluded from Net calculation: ${account.name}`);
      } else {
        // Deposit/investment account — asset
        totalBalance += effectiveBalance;
        console.log(`   → Added ${effectiveBalance} to ASSETS (total assets now: ${totalBalance})`);
      }
    });
    
    const netWorth = totalBalance - totalDebt;
    
    console.log(`🏛️ ${institutionName} Institution Summary RESULT:`, {
      totalAssets: totalBalance,
      totalDebt,
      netWorth: totalBalance - totalDebt,
      calculation: `${totalBalance} - ${totalDebt} = ${totalBalance - totalDebt}`
    });
    
    return {
      totalBalance,
      totalDebt,
      netWorth
    };
  };

  // Handle disconnecting from partner
  const handleDisconnectPartner = async () => {
    try {
      setDisconnecting(true);
      await removePartner();
      toast.success('Successfully disconnected from partner');
      // setShowDisconnectConfirm(false); // This modal is handled by the shared component
      setShowSharingModal(false);
      
      // Refresh all data
      await refreshUserData();
      await fetchWalletData();
      await fetchInvitations();
      
      // Force re-render by updating a state variable
      setActionInProgress(false);
    } catch (error) {
      console.error('Failed to disconnect partner:', error);
      
      // Handle specific case where partner is already disconnected
      if (error.response?.data?.message?.includes('You do not have a partner to remove')) {
        toast.info('Partner already disconnected. Refreshing your account status...');
        // setShowDisconnectConfirm(false); // This modal is handled by the shared component
        setShowSharingModal(false);
        
        // Force refresh user data to sync with server state
        try {
          await refreshUserData();
          await fetchWalletData();
          await fetchInvitations();
          toast.success('Account status updated successfully');
        } catch (refreshError) {
          console.error('Failed to refresh user data:', refreshError);
          toast.error('Please refresh the page to see updated status');
        }
      } else {
        toast.error(error.message || 'Failed to disconnect partner');
      }
    } finally {
      setDisconnecting(false);
    }
  };



  // Check and refresh partnership status
  const checkPartnershipStatus = async () => {
    try {
      // Refresh user data to get current partnership status
      await refreshUserData();
      console.log('✅ Partnership status refreshed');
    } catch (error) {
      console.error('Failed to refresh partnership status:', error);
    }
  };

  // Handle opening sharing management modal
  const handleOpenSharingModal = async () => {
    setShowSharingModal(true);
  };

  // Handler functions for the shared modal
  const handleSyncAccounts = async () => {
    try {
      if (DEBUG_WALLET) console.log('🔄 Manual sync accounts triggered');
      
      // Clear rate limiting for manual sync
      localStorage.removeItem('lastPlaidSync');
      localStorage.removeItem('plaidSyncStatus');
      
      await handleSyncPlaidAccounts();
      toast.success('Accounts synced successfully');
    } catch (error) {
      console.error('Failed to sync accounts:', error);
      toast.error('Failed to sync accounts');
    }
  };

  const handleViewActivityLog = () => {
    // console.log('View activity log');
    toast.info('Activity log feature coming soon!');
  };

  const handleManageSharingSettings = () => {
    console.log('📋 Opening Account Sharing Management Modal');
    setShowAccountSharingModal(true);
  };

  const handleViewSharedAccounts = () => {
    // console.log('View shared accounts');
    // Filter to show only shared accounts
    setActiveViewTab('our');
    setShowSharingModal(false);
    toast.success('Showing shared accounts view');
  };

  const handleAdvancedSettings = () => {
    // console.log('Advanced settings');
    toast.info('Advanced settings feature coming soon!');
  };

  // Get institution connection status
  const getInstitutionConnectionStatus = (accounts) => {
    const plaidAccounts = accounts.filter(account => account.isPlaidAccount);
    if (plaidAccounts.length === 0) return { status: 'manual', message: 'Manual Accounts' };
    
    // Check if accounts are stale (lastUpdated > 7 days ago)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const hasStaleAccounts = plaidAccounts.some(account => {
      // Check multiple possible date fields
      const lastUpdatedDate = account.lastUpdated || account.updatedAt || account.lastSyncDate;
      if (!lastUpdatedDate) {
        console.log('🔍 Account has no lastUpdated date:', account.name, account.institutionName);
        return true; // No lastUpdated means stale
      }
      const lastUpdated = new Date(lastUpdatedDate).getTime();
      if (isNaN(lastUpdated)) {
        console.log('🔍 Invalid lastUpdated date:', account.name, lastUpdatedDate);
        return true; // Invalid date means stale
      }
      const isStale = lastUpdated < sevenDaysAgo;
      if (isStale) {
        const daysSinceUpdate = Math.floor((Date.now() - lastUpdated) / (1000 * 60 * 60 * 24));
        console.log('🔍 Account is stale:', account.name || account.institutionName, 
          'Last updated:', new Date(lastUpdatedDate).toLocaleDateString(), 
          `(${daysSinceUpdate} days ago)`);
      }
      return isStale;
    });
    
    const statuses = plaidAccounts.map(account => {
      const rawName = account.institutionName || account.bankName || 'Unknown Institution';
      const normalize = (s) => (s || '').toLowerCase().replace(/\s+bank$/, '').trim();
      const keyName = normalize(rawName);
      // Try exact, normalized, and original
      let institutionStatus = syncStatus[rawName] || syncStatus[keyName];
      
      // Try fuzzy matching if exact match fails
      if (!institutionStatus && Object.keys(syncStatus).length > 0) {
        const syncKeys = Object.keys(syncStatus);
        const fuzzyMatch = syncKeys.find(key => {
          const keyLower = normalize(key);
          const nameLower = keyName;
          return keyLower.includes(nameLower) || nameLower.includes(keyLower);
        });
        
        if (fuzzyMatch) {
          institutionStatus = syncStatus[fuzzyMatch];
        }
      }
      
      // Check if account is stale (lastUpdated > 7 days)
      // Check multiple possible date fields
      const lastUpdatedDate = account.lastUpdated || account.updatedAt || account.lastSyncDate;
      if (lastUpdatedDate) {
        const lastUpdated = new Date(lastUpdatedDate).getTime();
        if (!isNaN(lastUpdated) && lastUpdated < sevenDaysAgo) {
          return 'expired'; // Mark as expired if stale
        }
      } else {
        // No lastUpdated date means account is stale
        return 'expired';
      }
      
    if (!institutionStatus) {
      // Fallback to initial items-based status if available
      const name = accounts[0]?.institutionName || accounts[0]?.bankName;
      if (name && initialInstitutionStatus[name]) {
        return initialInstitutionStatus[name].success ? 'connected' : 'unknown';
      }
      // If no sync status but account is stale, mark as expired
      return hasStaleAccounts ? 'expired' : 'unknown';
    }
      if (institutionStatus.success) {
        // Even if sync says success, check if account is stale
        return hasStaleAccounts ? 'expired' : 'connected';
      }
      if (institutionStatus.error === 'CONNECTION_EXPIRED') return 'expired';
      return 'disconnected';
    });
    
    // Determine overall status
    if (statuses.includes('expired')) return { status: 'expired', message: 'Reconnect' };
    if (statuses.includes('disconnected')) return { status: 'disconnected', message: 'Reconnect' };
    if (statuses.includes('unknown')) return { status: 'unknown', message: 'Reconnect' };
    if (statuses.every(status => status === 'connected')) return { status: 'connected', message: 'Connected' };
    
    return { status: 'mixed', message: 'Reconnect' };
  };

  // Get connection status styling
  const getConnectionStatusStyle = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'expired':
      case 'disconnected':
        return 'bg-red-100 text-red-800';
      case 'unknown':
      case 'mixed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Get connection status icon
  const getConnectionStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <LinkIcon className="h-3 w-3 mr-1" />;
      case 'expired':
        return <ExclamationTriangleIcon className="h-3 w-3 mr-1" />;
      case 'disconnected':
        return <XMarkIcon className="h-3 w-3 mr-1" />;
      case 'unknown':
      case 'mixed':
        return <QuestionMarkCircleIcon className="h-3 w-3 mr-1" />;
      default:
        return <LinkIcon className="h-3 w-3 mr-1" />;
    }
  };

  // Handle institution-level reconnect
  const handleInstitutionReconnect = (institutionName, accounts) => {
    // Find the itemId for this institution
    const institutionAccounts = accounts || [];
    const plaidAccount = institutionAccounts.find(acc => acc.isPlaidAccount && (acc.itemId || acc.plaidAccountId));
    const itemId = plaidAccount?.itemId || plaidAccount?.plaidAccountId || null;
    
    if (itemId) {
      setReconnectItemId(itemId);
      toast.info(`Reconnecting ${institutionName}...`);
    } else {
      toast.error(`Unable to find connection for ${institutionName}. Please connect as a new account.`);
    }
    setShowPlaidLink(true);
  };

  // Handle institution-level sharing toggle
  const handleInstitutionSharingToggle = async (institutionName, accounts) => {
    if (!hasPartner) {
      toast.error('Partner sharing is not available. Please set up a partner first.');
      return;
    }

    try {
      const sharedAccounts = accounts.filter(account => account.sharedWithPartner);
      const isCurrentlyShared = sharedAccounts.length > 0;
      const newSharingStatus = !isCurrentlyShared;

      toast.loading(`${newSharingStatus ? 'Sharing' : 'Unsharing'} all ${institutionName} accounts...`);

      // Update sharing settings for all accounts in the institution
      const updatePromises = accounts.map(account => {
        if (account.isPlaidAccount) {
          return walletService.updateSharingSettings(account._id, {
            showToPartner: newSharingStatus,
            showTransactions: newSharingStatus,
            showBalance: newSharingStatus,
            showInNetWorth: newSharingStatus
          });
        }
        return Promise.resolve({ success: true });
      });

      const results = await Promise.all(updatePromises);
      const failedUpdates = results.filter(result => !result.success);

      if (failedUpdates.length === 0) {
        toast.dismiss();
        toast.success(`${newSharingStatus ? 'Shared' : 'Unshared'} all ${institutionName} accounts successfully!`);
        
        // Add a small delay to ensure database has time to update before refreshing UI
        setTimeout(() => {
          fetchWalletData();
        }, 1000); // 1 second delay
      } else {
        toast.dismiss();
        toast.error(`Failed to update ${failedUpdates.length} accounts`);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error toggling institution sharing:', error);
      toast.error('Failed to update sharing settings');
    }
  };

  // Handle institution deletion
  const handleInstitutionDelete = async (institutionName, accounts) => {
    const confirmDelete = window.confirm(
      `⚠️ WARNING: This will permanently delete all ${accounts.length} accounts from ${institutionName} and their transaction history.\n\n` +
      `This action cannot be undone. Are you sure you want to proceed?`
    );

    if (!confirmDelete) return;

    const secondConfirm = window.confirm(
      `🚨 FINAL CONFIRMATION: You are about to delete:\n\n` +
      `• ${accounts.length} accounts from ${institutionName}\n` +
      `• All transaction history for these accounts\n` +
      `• All connection settings\n\n` +
      `Type "DELETE" in the next prompt to confirm.`
    );

    if (!secondConfirm) return;

    const finalConfirm = window.prompt(
      `Type "DELETE" (in capital letters) to confirm deletion of all ${institutionName} accounts:`
    );

    if (finalConfirm !== 'DELETE') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      toast.loading(`Deleting all ${institutionName} accounts...`);

      // Delete all accounts in the institution
      const deletePromises = accounts.map(account => {
        if (account.isPlaidAccount) {
          return walletService.disconnectPlaidAccount(account, true); // true = delete transactions
        } else {
          // Use deleteAccountWithTransactions to ensure all transactions are deleted
          return walletService.deleteAccountWithTransactions(account._id);
        }
      });

      const results = await Promise.all(deletePromises);
      const failedDeletes = results.filter(result => !result.success);

      if (failedDeletes.length === 0) {
        toast.dismiss();
        toast.success(`Successfully deleted all ${institutionName} accounts and data!`);
        fetchWalletData(); // Refresh data
      } else {
        toast.dismiss();
        toast.error(`Failed to delete ${failedDeletes.length} accounts`);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error deleting institution:', error);
      toast.error('Failed to delete institution accounts');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-screen-xl">
      {/* Page Header with Financial Overview */}
      <div className="flex flex-col md:flex-row justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Wallet</h1>
          <p className="text-gray-600">Manage your accounts and track your financial health</p>
        </div>
        
        <div className="mt-4 md:mt-0 flex items-center space-x-3">
          {/* Debug buttons - hidden but accessible via console commands */}
          {false && (
            <>
              <button
                onClick={handleSyncAccounts}
                className="flex items-center px-4 py-2.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 shadow-md hover:shadow-lg transition-all duration-200 h-10 text-sm"
                disabled={loading}
              >
                <ArrowPathIcon className="h-5 w-5 mr-1" />
                Debug Sync
              </button>
              
              <button
                onClick={debugSyncStatus}
                className="flex items-center px-4 py-2.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 shadow-md hover:shadow-lg transition-all duration-200 h-10 text-sm"
              >
                <InformationCircleIcon className="h-5 w-5 mr-1" />
                Debug Status
              </button>
            </>
          )}

          <button
            onClick={handleOpenSharingModal}
            className="flex items-center px-4 py-2.5 bg-orange-600 text-white rounded-md hover:bg-orange-700 shadow-md hover:shadow-lg transition-all duration-200 h-10"
          >
            <ShareIcon className="h-5 w-5 mr-1" />
            Sharing
          </button>

          <button
            onClick={() => setShowPlaidLink(true)}
            className="flex items-center px-4 py-2.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 shadow-md hover:shadow-lg transition-all duration-200 h-10"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            Connect Bank
          </button>
        </div>
      </div>

      {/* Statement Import Banner - TEMPORARILY HIDDEN */}
      {/* <div 
        className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-xl p-4 shadow-md cursor-pointer hover:shadow-lg transition-all group"
        onClick={() => setShowStatementImportModal(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className="flex-shrink-0 mr-4">
              <div className="p-3 bg-indigo-600 rounded-full group-hover:bg-indigo-700 transition-colors">
                <LightBulbIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-indigo-700 transition-colors">
                Unlock Deeper Insights
              </h3>
              <p className="text-sm text-gray-700">
                Load historical statements to analyze spending patterns and trends
              </p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowStatementImportModal(true);
            }}
            className="flex-shrink-0 ml-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm transition-colors shadow-md hover:shadow-lg"
          >
            Load Statements
          </button>
        </div>
      </div> */}

      {/* Financial Summary Cards - WALLET REPORT BAR SECTION */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mb-8">
        {/* Total Debt Card */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 p-1.5 sm:p-3 lg:p-4 rounded-xl shadow-md border border-red-200 hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between min-h-[140px] sm:min-h-[160px] lg:min-h-[180px] group relative">
          <div className="flex flex-col items-center mb-1 sm:mb-2">
            <div className="p-1 rounded-full bg-red-600 text-white mb-1">
              <CreditCardIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </div>
            <div className="w-full text-center">
              <h3 className="font-bold text-red-800 text-[10px] sm:text-sm lg:text-base xl:text-lg leading-tight inline-flex items-center justify-center gap-0.5 sm:gap-1">
                Total Debt
                <InformationCircleIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-red-600 cursor-help" title="Sum of all credit card balances and loan amounts owed" />
              </h3>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <p className="text-[10px] sm:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold text-center leading-tight break-words text-black">
              {formatCurrency(Math.abs(overallSummary.totalDebt))}
            </p>
          </div>
          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <p className="text-[9px] sm:text-xs lg:text-sm text-red-700 text-center leading-tight">Credit Cards & Loans</p>
            {/* Mortgage Toggle Button - Icon button at bottom, similar size to top icon */}
            <button
              onClick={() => setIncludeMortgagesInDebt(!includeMortgagesInDebt)}
              className={`p-1 rounded-full transition-all duration-200 flex items-center justify-center transform hover:scale-110 ${
                includeMortgagesInDebt 
                  ? 'bg-red-600 text-white hover:bg-red-700 shadow-md' 
                  : 'bg-amber-500 text-white hover:bg-amber-600 shadow-md'
              }`}
              title={includeMortgagesInDebt ? "Mortgages Included - Click to exclude" : "Mortgages Excluded - Click to include"}
            >
              <HomeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
            {includeMortgagesInDebt 
              ? "Sum of all credit card balances and loan amounts owed (including mortgages)" 
              : "Sum of all credit card balances and loan amounts owed (mortgages excluded)"}
          </div>
        </div>
        
        {/* Net Worth Card */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-1.5 sm:p-3 lg:p-4 rounded-xl shadow-md border border-green-200 hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between min-h-[140px] sm:min-h-[160px] lg:min-h-[180px] group relative">
          <div className="flex flex-col items-center mb-1 sm:mb-2">
            <div className="p-1 rounded-full bg-green-600 text-white mb-1">
              <CalculatorIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </div>
            <div className="w-full text-center">
              <h3 className="font-bold text-green-800 text-[10px] sm:text-sm lg:text-base xl:text-lg leading-tight inline-flex items-center justify-center gap-0.5 sm:gap-1">
                Net Worth
                <InformationCircleIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-green-600 cursor-help" title="Total Assets minus Total Debt" />
              </h3>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <p className={`text-[10px] sm:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold text-center leading-tight break-words ${isPositive(overallSummary.netWorth) ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(overallSummary.netWorth)}
            </p>
          </div>
          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <p className="text-[9px] sm:text-xs lg:text-sm text-green-700 text-center leading-tight">Assets minus Debts</p>
            {/* Home Equity Toggle Button - Icon button at bottom, similar size to top icon */}
            <button
              onClick={() => setIncludeMortgagesInNetWorth(!includeMortgagesInNetWorth)}
              className={`p-1 rounded-full transition-all duration-200 flex items-center justify-center transform hover:scale-110 ${
                includeMortgagesInNetWorth 
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-md' 
                  : 'bg-amber-500 text-white hover:bg-amber-600 shadow-md'
              }`}
              title={includeMortgagesInNetWorth ? "Home Equity Included - Click to exclude" : "Home Equity Excluded - Click to include"}
            >
              <HomeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </div>
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
            {includeMortgagesInNetWorth 
              ? "Total Assets - Total Debt (including mortgage & home equity)" 
              : "Total Assets - Total Debt (excluding mortgage & home equity)"}
          </div>
        </div>
        
        {/* Credit Utilization Card */}
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-1.5 sm:p-3 lg:p-4 rounded-xl shadow-md border border-amber-200 hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between min-h-[140px] sm:min-h-[160px] lg:min-h-[180px] group relative">
          <div className="flex flex-col items-center mb-1 sm:mb-2">
            <div className="p-1 rounded-full bg-amber-600 text-white mb-1">
              <ChartBarIcon className="h-3 w-3 sm:h-4 sm:w-4" />
            </div>
            <div className="w-full text-center">
              <h3 className="font-bold text-amber-800 text-[10px] sm:text-sm lg:text-base xl:text-lg leading-tight inline-flex items-center justify-center gap-0.5 sm:gap-1">
                Credit Utilization
                <InformationCircleIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-amber-600 cursor-help" title="Percentage of available credit being used. Keep under 30% for best credit score." />
              </h3>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <p className={`text-[10px] sm:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-bold text-center leading-tight ${overallSummary.creditUtilization <= 30 ? 'text-green-600' : 'text-amber-600'}`}>
              {overallSummary.creditUtilization.toFixed(1)}%
            </p>
          </div>
          <p className="text-[9px] sm:text-xs lg:text-sm text-amber-700 text-center leading-tight">
            {overallSummary.creditUtilization <= 30 ? 'Good (under 30%)' : 'Above recommended'}
          </p>
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
            Credit Used ÷ Total Credit Available × 100
          </div>
        </div>
      </div>

      {/* Partner Menu - Mobile Friendly */}
      {hasPartner ? (
        <MobileViewSelector
          title="Views"
          currentView={activeViewTab}
          onViewChange={(view) => setActiveViewTab(view)}
          sharingButton={
            <button
              onClick={handleOpenSharingModal}
              className="rounded-lg bg-orange-600 hover:bg-orange-700 text-white transition-colors flex-shrink-0 flex items-center justify-center"
              style={{ padding: '4.8px' }}
              aria-label="Sharing Settings"
            >
              <ShareIcon className="h-4 w-4" />
            </button>
          }
          views={[
            {
              value: 'my',
              label: 'My Wallet',
              icon: <UserIcon className="h-5 w-5" />
            },
            {
              value: 'our',
              label: 'Our Wallet',
              icon: <UserGroupIcon className="h-5 w-5" />
            },
            {
              value: 'partner',
              label: `${partnerName || 'Partner'}'s Wallet`,
              icon: <UserIcon className="h-5 w-5" />
            }
          ]}
        />
      ) : null}

      {/* Account Type Filter - Mobile Friendly Icon-Only Selector */}
      <MobileIconSelector
        title="Accounts"
        currentValue={activeTab}
        onChange={(value) => setActiveTab(value)}
        options={[
          { 
            value: 'all', 
            label: 'All Accounts', 
            icon: <BuildingLibraryIcon className="h-5 w-5" />
          },
          { 
            value: 'checking', 
            label: 'Checking', 
            count: getGroupedAccounts(filteredAccounts).checking.length, 
            icon: <BanknotesIcon className="h-5 w-5" />
          },
          { 
            value: 'savings', 
            label: 'Savings', 
            count: getGroupedAccounts(filteredAccounts).savings.length, 
            icon: <BuildingLibraryIcon className="h-5 w-5" />
          },
          { 
            value: 'credit', 
            label: 'Credit Cards', 
            count: getGroupedAccounts(filteredAccounts).credit.length, 
            icon: <CreditCardIcon className="h-5 w-5" />
          },
          { 
            value: 'loans', 
            label: 'Loans', 
            count: getGroupedAccounts(filteredAccounts).loan.length, 
            icon: <HomeIcon className="h-5 w-5" />
          },
          { 
            value: 'investments', 
            label: 'Investments', 
            count: getGroupedAccounts(filteredAccounts).investment.length, 
            icon: <ChartBarIcon className="h-5 w-5" />
          },
        ]}
      />

      {/* Main content with side-rail */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: account list */}
        <div className="lg:col-span-9 space-y-6">
        {isLoading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <CreditCardIcon className="h-8 w-8 text-indigo-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No accounts found</h3>
            <p className="text-gray-500 mb-6">
              {!hasPartner || activeViewTab === 'my' 
                ? "You haven't added any accounts yet." 
                : activeViewTab === 'our'
                  ? "No joint accounts found. Create or mark accounts as joint."
                  : `${partnerName} hasn't shared any accounts yet.`}
            </p>
            
            {(!hasPartner || activeViewTab === 'my') && (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 justify-center">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 shadow-md"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                Add Account Manually
              </button>
            <button 
                onClick={() => setShowPlaidLink(true)}
                className="flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 shadow-md"
            >
                <BuildingLibraryIcon className="h-5 w-5 mr-1" />
                Connect Bank
            </button>
            </div>
            )}
            
            {hasPartner && activeViewTab === 'our' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 shadow-md"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                Create Joint Account
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Display account cards grouped by institution */}
            {(() => {
              const accountsByInstitution = getAccountsByInstitution(filteredAccounts);
              const institutionNames = Object.keys(accountsByInstitution).sort();
              
              return institutionNames.map((institutionName) => (
                <div key={institutionName} className="mb-8">
                  {/* Institution Container/Wrapper - Enhanced with stronger shadows and borders */}
                  <div className="bg-white rounded-xl shadow-xl border-2 border-gray-300 overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                    {/* Institution Header */}
                    <div className="p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300 overflow-hidden">
                      {/* First Row: Institution Name and Icon */}
                      <div className="flex items-center justify-between mb-2 sm:mb-0">
                        <div className="flex items-center min-w-0 flex-1">
                        <div className="p-2 sm:p-3 rounded-full bg-indigo-100 mr-2 sm:mr-4 shadow-md flex items-center justify-center flex-shrink-0">
                          {(() => {
                            const clean = (str = '') => (str || '')
                              .toLowerCase()
                              .replace(/\./g, '')
                              .replace(/\b(national|association|assn|bank|banking|usa|us|u\s*s|na|n\s*a)\b/g, '')
                              .replace(/[^a-z0-9]+/g, '')
                              .trim();
                            const key = clean(institutionName);
                            const logo = institutionBranding[key]?.logo;
                            return logo ? (
                            // eslint-disable-next-line jsx-a11y/alt-text
                            <img src={logo} className="h-5 w-5 sm:h-6 sm:w-6 object-contain rounded" />
                            ) : (
                            <BuildingLibraryIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
                            );
                          })()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-xl font-semibold text-gray-900 truncate" style={{
                            fontSize: 'clamp(14px, 4vw, 20px)',
                            fontWeight: 600
                          }}>
                            {institutionName}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate" style={{
                            fontSize: 'clamp(11px, 3vw, 14px)'
                          }}>
                            {accountsByInstitution[institutionName].length} account{accountsByInstitution[institutionName].length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      
                        {/* Connection Status + Gear Icon (top-right row) */}
                        {(() => {
                          const connectionStatus = getInstitutionConnectionStatus(accountsByInstitution[institutionName]);
                          const accounts = accountsByInstitution[institutionName];
                          return (
                            <div className="ml-2 sm:ml-4 flex items-center space-x-2 flex-shrink-0">
                              {/* Mobile: Icon only with tooltip */}
                              <div className="sm:hidden relative group">
                                <span className={`inline-flex items-center justify-center p-1.5 rounded-full ${getConnectionStatusStyle(connectionStatus.status)}`}>
                                  {getConnectionStatusIcon(connectionStatus.status)}
                                </span>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  {connectionStatus.message}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                              {/* Desktop: Full badge */}
                              <span className={`hidden sm:inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getConnectionStatusStyle(connectionStatus.status)}`}>
                                {getConnectionStatusIcon(connectionStatus.status)}
                                <span>{connectionStatus.message}</span>
                              </span>
                              {/* Settings Gear Icon (always to the right of badge) */}
                              <div className="relative institution-dropdown-container">
                                <button
                                  ref={el => buttonRefs.current[institutionName] = el}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const position = calculateDropdownPosition(institutionName);
                                    setDropdownPositions(prev => ({
                                      ...prev,
                                      [institutionName]: position
                                    }));
                                    setOpenDropdowns(prev => ({
                                      ...prev,
                                      [institutionName]: !prev[institutionName]
                                    }));
                                  }}
                                  className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                                  title="Institution Settings"
                                >
                                  <CogIcon className="h-5 w-5 text-gray-500" />
                                </button>

                                {/* Dropdown Menu */}
                                {openDropdowns[institutionName] && dropdownPositions[institutionName] && (
                                <Portal>
                                  <div
                                    ref={el => dropdownRefs.current[institutionName] = el}
                                    style={dropdownPositions[institutionName]}
                                    className="fixed w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]"
                                    onClick={(e) => { e.stopPropagation(); }}
                                  >
                                    <div className="py-1">
                                      {/* Reconnect Option */}
                                      {(() => {
                                        const cs = getInstitutionConnectionStatus(accounts);
                                        if (cs.status === 'expired' || cs.status === 'disconnected' || cs.status === 'unknown') {
                                          return (
                                            <button
                                              onClick={() => {
                                                handleInstitutionReconnect(institutionName, accounts);
                                                setOpenDropdowns(prev => ({ ...prev, [institutionName]: false }));
                                              }}
                                              className="flex items-center w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50 hover:text-blue-900"
                                            >
                                              <ArrowPathIcon className="h-4 w-4 mr-3" />
                                              Reconnect
                                            </button>
                                          );
                                        }
                                        return null;
                                      })()}

                                      {/* Share All Accounts */}
                                      {hasPartner && (
                                        <button
                                          onClick={() => {
                                            handleInstitutionSharingToggle(institutionName, accounts);
                                            setOpenDropdowns(prev => ({ ...prev, [institutionName]: false }));
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                          <ShareIcon className="h-4 w-4 mr-3" />
                                          {accounts.some(acc => acc.sharedWithPartner) ? 'Unshare All Accounts' : 'Share All Accounts'}
                                        </button>
                                      )}

                                      {/* Delete Institution */}
                                      <button
                                        onClick={() => {
                                          handleInstitutionDelete(institutionName, accounts);
                                          setOpenDropdowns(prev => ({ ...prev, [institutionName]: false }));
                                        }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-900"
                                      >
                                        <TrashIcon className="h-4 w-4 mr-3" />
                                        Delete Institution
                                      </button>
                                    </div>
                                  </div>
                                </Portal>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
          
                      {/* Second Row (Mobile) / Same Row (Desktop): Net Worth and Controls */}
                      {(() => {
                        const institutionSummary = getInstitutionSummary(accountsByInstitution[institutionName]);
                        const connectionStatus = getInstitutionConnectionStatus(accountsByInstitution[institutionName]);
                        const accounts = accountsByInstitution[institutionName];
                        
                        return (
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between sm:justify-start gap-2 sm:gap-4 sm:mt-2 overflow-hidden">
                            {/* Net Worth */}
                            <div className={`flex items-center gap-1 font-semibold truncate min-w-0 ${institutionSummary.netWorth >= 0 ? 'text-green-600' : 'text-red-600'}`} style={{
                              fontSize: 'clamp(12px, 3.5vw, 16px)',
                              fontWeight: 600
                            }}>
                              <span className="whitespace-nowrap">Net: {formatCurrency(institutionSummary.netWorth)}</span>
                              <div className="relative group flex-shrink-0">
                                <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
                                  <p className="font-semibold mb-1 text-white">Institution Net</p>
                                  <p className="text-gray-300">Deposits &amp; investments minus credit card balances. Loans are listed separately and excluded from this figure.</p>
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Reconnect Button - Show when connection issues */}
                            {(connectionStatus.status === 'expired' || connectionStatus.status === 'disconnected' || connectionStatus.status === 'unknown') && (
                              <button
                                onClick={() => handleInstitutionReconnect(institutionName, accounts)}
                                className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
                              >
                                <ArrowPathIcon className="h-4 w-4 mr-1" />
                                Reconnect
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Institution's Accounts - Grouped by type */}
                    <div className="p-4 sm:p-6 lg:p-8 bg-white">
                      {(() => {
                        const instAccounts = accountsByInstitution[institutionName];
                        const isChecking = (a) => (a.type === 'depository' && a.subtype === 'checking') || a.accountType?.toLowerCase() === 'checking';
                        const isSavings = (a) => (a.type === 'depository' && a.subtype === 'savings') || a.accountType?.toLowerCase() === 'savings';
                        const isCredit = (a) => a.type === 'credit' || a.subtype === 'credit card' || a.accountType?.toLowerCase().includes('credit');
                        const isLoanOrLine = (a) => a.type === 'loan' || ['loan','mortgage','student','auto','personal','line of credit'].includes((a.subtype||'').toLowerCase());

                        const groups = [
                          { title: 'Checking', items: instAccounts.filter(isChecking) },
                          { title: 'Savings', items: instAccounts.filter(isSavings) },
                          { title: 'Credit Cards', items: instAccounts.filter(isCredit) },
                          { title: 'Loans & Lines', items: instAccounts.filter(isLoanOrLine) }
                        ];

                        return groups.filter(g => g.items.length > 0).map((group) => (
                          <div key={`${institutionName}-${group.title}`} className="mb-6">
                            <div className="flex items-center justify-between mb-2 overflow-hidden min-w-0">
                              <h4 className="text-xs sm:text-sm font-semibold text-gray-800 truncate min-w-0 flex-1" style={{
                                fontSize: 'clamp(12px, 3vw, 14px)',
                                fontWeight: 600
                              }}>
                                {group.title}
                              </h4>
                              {(() => {
                                const sum = group.items.reduce((acc, a) => {
                                  const val = Math.abs(a.currentBalance || a.availableBalance || a.balance || 0);
                                  return acc + (isNaN(val) ? 0 : val);
                                }, 0);
                                const isDebt = group.title === 'Credit Cards' || group.title === 'Loans & Lines';
                                return (
                                  <span className={`text-[10px] sm:text-xs font-semibold whitespace-nowrap ml-2 flex-shrink-0 ${isDebt ? 'text-red-600' : 'text-gray-700'}`} style={{
                                    fontSize: 'clamp(10px, 2.5vw, 12px)',
                                    fontWeight: 600
                                  }}>
                                    Total: { (sum || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-6 items-stretch">
                              {group.items.map((account) => {
                                return (
                                  <div 
                                    key={account._id || account.id} 
                                    className={`w-full h-full`}
                                    data-account-id={account._id}
                                  >
                                    <div className="shadow-2xl hover:shadow-3xl transition-shadow duration-300 rounded-xl h-full">
                                      <WalletCard
                                        account={account}
                                        isOwnedByUser={account.__isOwnedByUser}
                                        viewMode={activeViewTab}
                                        onUpdate={fetchWalletData}
                                        onEdit={(account) => console.log('Edit account:', account)}
                                        onDelete={async (account) => {
                                          try {
                                            const result = account.isPlaidAccount 
                                              ? await walletService.disconnectPlaidAccount(account, true)
                                              : await walletService.deleteAccount(account._id);
                                            if (result.success) {
                                              toast.success('Account deleted successfully');
                                              fetchWalletData();
                                            } else {
                                              toast.error(result.error || 'Failed to delete account');
                                            }
                                          } catch (error) {
                                            console.error('Error deleting account:', error);
                                            toast.error('Failed to delete account');
                                          }
                                        }}
                                        onMarkAsJoint={handleMarkAsJoint}
                                        onUpdateSharingSettings={handleUpdateSharingSettings}
                                        hasPartner={hasPartner}
                                        actionInProgress={actionInProgress}
                                        syncStatus={syncStatus}
                                        institutionLogo={account.institutionBranding?.logo}
                                        onReconnect={() => {
                                          const itemId = account.itemId || null;
                                          if (itemId) {
                                            setReconnectItemId(itemId);
                                          }
                                          setShowPlaidLink(true);
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </>
        )}
        </div>

        {/* Right: side-rail */}
        <aside className="lg:col-span-3 space-y-4">
          {/* Insights */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Insights</h3>
            <ul className="space-y-2 text-sm">
              {expenseStats?.largestCategory?.name ? (
                <li className="flex items-start">
                  <span className="mt-0.5 mr-2 h-2 w-2 rounded-full bg-amber-500"></span>
                  Top spending category this month: <span className="ml-1 font-medium">{expenseStats.largestCategory.name}</span>
                </li>
              ) : (
                <li className="text-gray-500">No insights yet.</li>
              )}
            </ul>
          </div>

          {/* Budgets */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Budgets</h3>
            {budgetStats ? (
              <div className="text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Budgeted</span><span className="font-medium">${Number(budgetStats.totalBudgeted || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Spent</span><span className="font-medium">${Number(budgetStats.spent || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Remaining</span><span className="font-medium">${Number(budgetStats.remaining || 0).toLocaleString()}</span></div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No budget data.</div>
            )}
          </div>

          {/* Expenses */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Expenses (MTD)</h3>
            {expenseStats ? (
              <div className="text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Total</span><span className="font-medium">${Number(expenseStats.totalMonthlyExpenses || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Avg/day</span><span className="font-medium">${Number(expenseStats.averageDailySpend || 0).toFixed(0)}</span></div>
              </div>
            ) : (
              <div className="text-sm text-gray-500">No expense data.</div>
            )}
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Recent Transactions</h3>
            <ul className="divide-y divide-gray-100">
              {recentTransactions && recentTransactions.length > 0 ? (
                recentTransactions.map((tx) => (
                  <li key={tx._id} className="py-2 text-sm flex justify-between">
                    <span className="truncate mr-2 text-gray-700">{tx.description}</span>
                    <span className={`${(tx.amount < 0) ? 'text-red-600' : 'text-green-600'} font-medium`}>{(tx.amount < 0 ? '-' : '+')}${Math.abs(tx.amount || 0).toFixed(2)}</span>
                  </li>
                ))
              ) : (
                <li className="py-2 text-sm text-gray-500">No recent activity.</li>
              )}
            </ul>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-gradient-to-br from-white to-blue-50 p-3 rounded-xl border border-blue-200 flex flex-col items-center justify-center hover:shadow-md transition-all duration-200"
              >
                <div className="p-1.5 rounded-full bg-blue-100 mb-1.5">
                  <PlusIcon className="h-5 w-5 text-blue-600" />
                </div>
                <span className="text-xs font-medium text-blue-700 text-center leading-tight">Add Account</span>
              </button>

              <button
                onClick={() => setShowPlaidLink(true)}
                className="bg-gradient-to-br from-white to-green-50 p-3 rounded-xl border border-green-200 flex flex-col items-center justify-center hover:shadow-md transition-all duration-200"
              >
                <div className="p-1.5 rounded-full bg-green-100 mb-1.5">
                  <BuildingLibraryIcon className="h-5 w-5 text-green-600" />
                </div>
                <span className="text-xs font-medium text-green-700 text-center leading-tight">Connect Bank</span>
              </button>

              <SyncButton
                onSyncSuccess={handleSyncSuccess}
                onSyncError={handleSyncError}
                compact
              />

              <button
                className="bg-gradient-to-br from-white to-amber-50 p-3 rounded-xl border border-amber-200 flex flex-col items-center justify-center hover:shadow-md transition-all duration-200"
              >
                <div className="p-1.5 rounded-full bg-amber-100 mb-1.5">
                  <LightBulbIcon className="h-5 w-5 text-amber-600" />
                </div>
                <span className="text-xs font-medium text-amber-700 text-center leading-tight">Financial Tips</span>
              </button>
            </div>
          </div>
        </aside>
      </div>
      
      {/* Modals and Dialogs */}
      {showAddModal && (
        <AccountAddModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onManualAdd={() => {
            // TODO: Implement manual add form
            toast('Manual account entry coming soon!', {
              icon: 'ℹ️',
              duration: 3000,
            });
            setShowAddModal(false);
          }}
          onUploadStatement={() => {
            // Refresh wallet data after statement upload
            fetchWalletData();
          }}
        />
      )}
      
      {showPlaidLink && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-bold text-black">Connect Financial Accounts</h3>
                <button onClick={() => setShowPlaidLink(false)} className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 sm:p-6">
                <PlaidConnectionOptions 
                  onSuccess={handlePlaidSuccess}
                  onExit={() => {
                    setShowPlaidLink(false);
                    setReconnectItemId(null);
                  }}
                  reconnectItemId={reconnectItemId}
                />
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Standardized Sharing Management Modal */}
      <SharingManagementModal
        isOpen={showSharingModal}
        onClose={() => {
          setShowSharingModal(false);
          // Refresh wallet data after modal closes (in case partnership changed)
          fetchWalletData();
          refreshUserData();
        }}
        user={user}
        onSyncAccounts={handleSyncAccounts}
        onViewActivityLog={handleViewActivityLog}
        onManageSharingSettings={handleManageSharingSettings}
        onViewSharedAccounts={handleViewSharedAccounts}
        onAdvancedSettings={handleAdvancedSettings}
      />

      {/* Account Sharing Management Modal */}
      <AccountSharingModal
        isOpen={showAccountSharingModal}
        onClose={() => setShowAccountSharingModal(false)}
        accounts={accounts}
        partnerName={partnerName}
        onUpdate={fetchWalletData}
      />

      {/* Statement Import Modal - TEMPORARILY HIDDEN */}
      {/* <StatementImportModal
        isOpen={showStatementImportModal}
        onClose={() => setShowStatementImportModal(false)}
        accounts={[...plaidAccounts, ...Object.values(accounts).flat()].filter(acc => acc._id)}
        onSuccess={() => {
          // Refresh wallet data after successful import
          fetchWalletData();
          toast.success('Transactions imported successfully!');
        }}
      /> */}
    </div>
  );
};

export default Wallet; 